import * as cmd from './cmd'
import * as cvar from './cvar'
import * as GL from './GL'
import * as vec from './vec'
import * as cl from './cl'
import * as mod from './mod'
import * as chase from './chase'
import * as v from './v'
import * as scr from './scr'
import * as sys from './sys'
import * as con from './console'
import * as host from './host'
import * as com from './com'
import * as sv from './sv'
import * as pr from './pr'
import * as q from './q'
import * as vid from './vid'
import * as msg from './msg'
import * as def from './def'
import * as batchRender from './batchRender'
import * as s from './s'
import * as lm from './lightmap'
import * as tx from './texture'
import * as mapAlpha from './mapAlpha'
import * as fog from './fog'
import * as sky from './sky'
import * as pscript from './pscript'
import { AliasFrame, AliasFrameGroup, Face, Leaf, Model, Node, NodeLeaf, Plane, SpriteFrame, SpriteFrameGroup, TexChain, Texture } from './types/Model'
import { V3, V4 } from './types/Vector'
import { EFrags } from './types/Model'
import { Entity, LightCache } from './types/Entity'
import { DynamicLight } from './cl'
import { createAttribParam } from './GL'

export const MAX_DLIGHTS = 32
export const LERP = {
	movestep: 1,
	resetanim: 1 << 1,
	resetanim2: 1 << 2,
	resetmove: 1 << 3,
	finish: 1 << 4
}

type Refdef = {
	vrect: {
		x: number,
		y: number,
		width: number,
		height: number
	},
	vieworg: V3,
	viewangles: V3
	fov_y: number
	fov_x: number
}

type RState = {
	framecount: number
	pefragtopnode: Leaf | Node | null
	dlightvecs: WebGLBuffer,
	warpbuffer: WebGLBuffer,
	warprenderbuffer: WebGLBuffer,
	warptexture: WebGLTexture,
	notexture_mip: Texture,
	solidskytexture: WebGLTexture,
	alphaskytexture: WebGLTexture,
	null_texture: WebGLTexture,
	vis_changed: boolean,
	visframecount: number,
	frustum: [Plane, Plane, Plane, Plane],
	// Flat mirror of state.frustum, rebuilt each setFrustum() call: 4 planes
	// x (nx, ny, nz, dist); frustumSignbits[k] bit-encodes which normal
	// components of plane k are negative (same convention as Plane.signbits).
	frustumFlat: Float32Array,
	frustumSignbits: Uint8Array,
	// Advanced once per markWorldFrustum() call; surfVisibleFrame stamps compare
	// against this to tell "marked this frame" from "marked N frames ago".
	frustumFrame: number,
	// Explicit (idx, mask) pair stack for the iterative frustum walk; sized far
	// past any BSP tree depth.
	frustumWalkStack: Int32Array,
	refdef: Refdef,
	vup: V3,
	vpn: V3,
	vright: V3,
	perspective: number[],
	dowarp: boolean,
	warpwidth: number,
	warpheight: number,
	oldwarpwidth: number,
	oldwarpheight: number,
	warpSupported: boolean,
	warpDepthFormat: number,
	warpDepthAttachment: number,
	viewleaf: Leaf | null,
	c_brush_verts: number,
	c_alias_polys: number,
	// r_speeds stage timings (ms) + chain-rebuild count for the current frame
	rs_markms: number,
	rs_walkms: number,
	rs_rebuilds: number,
	// worldmodel whose GL geometry (VBO + lightmap textures) is already built for
	// the current GL context; lets newMap skip the rebuild on a same-map respawn
	builtWorldmodel: Model | null,
	skytexturenum: number
	avertexnormals: V3[],
	ramp1: number[],
	ramp2: number[],
	ramp3: number[],
	numparticles: number,
	avelocities: V3[],
	particleOrg: Float32Array,
	particleVel: Float32Array,
	particleRamp: Float32Array,
	particleDie: Float32Array,
	particleColor: Uint8Array,
	particleType: Uint8Array,
	numActiveParticles: number,
	particleInstanceData: ArrayBuffer,
	particleInstanceFloats: Float32Array,
	particleInstanceBytes: Uint8Array,
	particleCornerBuffer: WebGLBuffer,
	particleInstanceBuffer: WebGLBuffer,
	tracercount: number
	lightmap_modified: boolean[]
	drawsky: boolean
	model_vbo: WebGLBuffer
	cl_worldmodel: Model
	oldviewleaf: Leaf
	mod_novis: Uint8Array
	mod_novis_capacity: number
	// persistent decompress target for leafPVS -- rebuilds happen every leaf crossing
	// while moving, and a fresh vis row per rebuild was the top steady-state allocator
	leafpvs_scratch: Uint8Array
	fatbytes: number
	fatpvs: Uint8Array
	fatpvs_capacity: number;
	fatpvs_scratch: Uint8Array
	skyvecs: WebGLBuffer
	cached_vis: Uint8Array
	viewAnglesRad: V3
	viewMatrix: number[]
	activeDlights: Int32Array
	numActiveDlights: number
	// Packed per-frame dlight uniforms for the Brush fragment shader (GPU dlighting):
	// vec4(origin.xyz, radius) and vec4(color.rgb, minlight) per light, MAX_DLIGHTS slots.
	// numShaderDlights is 0 when flashblend or r_dynamic 0 disable surface dlighting.
	dlightPosRadius: Float32Array
	dlightColor: Float32Array
	numShaderDlights: number
	// framecount of the last dlight uniform upload — drawTextureChains runs per
	// brush entity, but the packed arrays only change once per frame.
	dlightUniformFrame: number
	// Persistent hot-path temporaries, consumed immediately at their single call
	// site — never held across calls.
	cullMins: V3
	cullMaxs: V3
	// Saved main-view origin/angles while the skyroom sub-view borrows refdef (renderView).
	skyroomSaveOrg: V3
	skyroomSaveAng: V3
	// setupAliasFrame() result: byte offsets of the two poses to blend between in
	// the alias model's cmds VBO, and the blend factor. Mutated in place per draw.
	aliasLerp: { pose1ofs: number, pose2ofs: number, blend: number }
}

export const state: RState = {
	framecount: 0,
	pefragtopnode: null,
	dlightvecs: null,
	warpbuffer: null,
	warprenderbuffer: null,
	warptexture: null,
	vis_changed: false,
	notexture_mip: null,
	solidskytexture: null,
	alphaskytexture: null,
	null_texture: null,
	visframecount: 0,
	frustumFlat: new Float32Array(16),
	frustumSignbits: new Uint8Array(4),
	frustumFrame: 0,
	frustumWalkStack: new Int32Array(2048),
	warpwidth: 0,
	warpheight: 0,
	oldwarpwidth: 0,
	oldwarpheight: 0,
	warpSupported: true,
	warpDepthFormat: 0,
	warpDepthAttachment: 0,
	c_brush_verts: 0,
	c_alias_polys: 0,
	rs_markms: 0,
	rs_walkms: 0,
	rs_rebuilds: 0,
	builtWorldmodel: null,
	frustum: [{
		normal: [0, 0, 0],
		dist: 0,
		signbits: 0,
		type: 0
	}, {
		normal: [0, 0, 0],
		dist: 0,
		signbits: 0,
		type: 0
	}, {
		normal: [0, 0, 0],
		dist: 0,
		signbits: 0,
		type: 0
	}, {
		normal: [0, 0, 0],
		dist: 0,
		signbits: 0,
		type: 0
	}],
	vup: [0,0,0],
	vpn: [0,0,0],
	vright: [0,0,0],	
	refdef: {
		vrect: {
			x: 0,
			y: 0,
			width: 0,
			height: 0
		},
		vieworg: [0.0, 0.0, 0.0],
		viewangles: [0.0, 0.0, 0.0],
		fov_y: 0,
		fov_x: 0
	},
	perspective: [
		0.0, 0.0, 0.0, 0.0,
		0.0, 0.0, 0.0, 0.0,
		0.0, 0.0, -65540.0 / 65532.0, -1.0,
		0.0, 0.0, -524288.0 / 65532.0, 0.0
	],
	dowarp: false,
	viewleaf: null,
	skytexturenum: 0,
	avertexnormals: [],
	ramp1: [],
	ramp2: [],
	ramp3: [],
	numparticles: 0,
	avelocities: [],
	particleOrg: new Float32Array(0),
	particleVel: new Float32Array(0),
	particleRamp: new Float32Array(0),
	particleDie: new Float32Array(0),
	particleColor: new Uint8Array(0),
	particleType: new Uint8Array(0),
	numActiveParticles: 0,
	particleInstanceData: new ArrayBuffer(0),
	particleInstanceFloats: new Float32Array(0),
	particleInstanceBytes: new Uint8Array(0),
	particleCornerBuffer: null,
	particleInstanceBuffer: null,
	tracercount: 0,
	lightmap_modified: [],
	drawsky: false,
	model_vbo: null,
	cl_worldmodel: null,
	oldviewleaf: null,
	mod_novis: null,
	mod_novis_capacity: 0,
	leafpvs_scratch: null,
	fatbytes: 0,
	fatpvs: null,
	fatpvs_capacity: 0,
	fatpvs_scratch: null,
	skyvecs: null,
	cached_vis: null,
	viewAnglesRad: [0.0, 0.0, 0.0],
	viewMatrix: [0.0, 0.0, 0.0, 0.0, 0.0, 0.0, 0.0, 0.0, 0.0],
	activeDlights: new Int32Array(MAX_DLIGHTS),
	numActiveDlights: 0,
	dlightPosRadius: new Float32Array(MAX_DLIGHTS * 4),
	dlightColor: new Float32Array(MAX_DLIGHTS * 4),
	numShaderDlights: 0,
	dlightUniformFrame: -1,
	cullMins: [0.0, 0.0, 0.0],
	cullMaxs: [0.0, 0.0, 0.0],
	skyroomSaveOrg: [0.0, 0.0, 0.0],
	skyroomSaveAng: [0.0, 0.0, 0.0],
	aliasLerp: { pose1ofs: 0, pose2ofs: 0, blend: 1.0 }
}

export const cvr = {
} as any

const	MAXLIGHTMAPS = 16
export type Color = [number, number, number]
// set on init
// let gl: any = null

// efrag

export const splitEntityOnNode = function (node: Node | Leaf, entity: Entity, emins: V3, emaxs: V3) {
	if (node.contents === mod.CONTENTS.solid)
		return;

	// add an efrag if the node is a leaf
	if (node.contents < 0) {
		const leaf = node as Leaf
		if (!state.pefragtopnode)
			state.pefragtopnode = leaf;

		const efrags = {
			leafnext: leaf.efrags,
			entity
		}

		leaf.efrags = efrags

		return;
	}
	const _node = node as Node
	var sides = vec.boxOnPlaneSide(emins, emaxs, _node.plane);
	if (sides === 3) {
		// split on this plane
		// if this is the first splitter of this bmodel, remember it
		if (!state.pefragtopnode)
			state.pefragtopnode = _node;
	}

	if ((sides & 1) !== 0)
		splitEntityOnNode(_node.children[0], entity, emins, emaxs);
	if ((sides & 2) !== 0)
		splitEntityOnNode(_node.children[1], entity, emins, emaxs);
};

const storeEfrags = (efrag: EFrags) => {
	for(var _efrag = efrag; !!_efrag; _efrag = _efrag.leafnext) {
		var ent = _efrag.entity
		if (ent.visframe !== host.state.framecount && cl.state.numvisedicts < def.max_vis_edicts) {
			cl.state.visedicts[cl.state.numvisedicts++] = ent
			ent.visframe = host.state.framecount
		}
	}
}

// light
/*
==================
R_AnimateLight
==================
*/
const animateLight = () => {
	//
	// light animations
	// 'm' is normal light, 'a' is no light, 'z' is double bright
	var t = cl.clState.time * 10.0;
	var base = Math.floor(t);
	var i = base;
	var f = t - base;
	if (cvr.lerplightstyles.value === 0)
		f = 0.0;

	for (var j = 0; j < lm.MAX_LIGHTSTYLES; j++) {
		var val
		if (cvr.fullbright.value === 1 || cl.state.lightstyle[j].length === 0) {
			val = 264;
		}
		else {
			var idx = i % cl.state.lightstyle[j].length;
			var next = idx + 1;
			if (next === cl.state.lightstyle[j].length)
				next = 0;
			var k = cl.state.lightstyle[j].charCodeAt(idx) - 97; // 'a'
			var n = cl.state.lightstyle[j].charCodeAt(next) - 97; // 'a'
			// only interpolate abrupt changes (e.g. flickering light in e1m1) if r_lerplightstyles >= 2
			if (cvr.lerplightstyles.value < 2 && Math.abs(n - k) >= 6)
				n = k;
			val = (k * 22 + (n - k) * 22 * f) | 0;
		}
		//johnfitz
		if (lm.state.lightstylevalue[j] !== val) {
			lm.state.lightstylevalue[j] = val;
			lm.state.lightstyle_uniform_dirty = true
		}
	}
}

export const renderDlights = function () {
	const gl = GL.getContext()
	if (cvr.flashblend.value === 0)
		return;
	gl.enable(gl.BLEND);
	var program = GL.useProgram('Dlight'), l, a;
	gl.bindBuffer(gl.ARRAY_BUFFER, state.dlightvecs);
	gl.vertexAttribPointer(program.attributeMap.aPosition.location, 3, gl.FLOAT, false, 0, 0); // TODO: is this fixed now?
	for (var i = 0; i <= 31; ++i) {
		l = cl.state.dlights[i];
		if ((l.die < cl.clState.time) || (l.radius === 0.0))
			continue;
		if (vec.length([l.origin[0] - state.refdef.vieworg[0], l.origin[1] - state.refdef.vieworg[1], l.origin[2] - state.refdef.vieworg[2]]) < (l.radius * 0.35)) {
			a = l.radius * 0.0003;
			v.blend[3] += a * (1.0 - v.blend[3]);
			a /= v.blend[3];
			v.blend[0] = v.blend[1] * (1.0 - a) + (255.0 * a);
			v.blend[1] = v.blend[1] * (1.0 - a) + (127.5 * a);
			v.blend[2] *= 1.0 - a;
			continue;
		}
		gl.uniform3fv(program.uniforms.uOrigin, l.origin);
		gl.uniform1f(program.uniforms.uRadius, l.radius);
		gl.drawArrays(gl.TRIANGLE_FAN, 0, 18);
	}
	gl.disable(gl.BLEND);
};

export const gatherDlights = () => {
	state.numActiveDlights = 0;
	for (var i = 0; i < cl.state.dlights.length; ++i) {
		var dl = cl.state.dlights[i];
		if ((dl.die >= cl.clState.time) && (dl.radius !== 0.0))
			state.activeDlights[state.numActiveDlights++] = i;
	}

	// Pack active dlights for the Brush fragment shader's analytic accumulation.
	// Flashblend (glow-ball rendering) and r_dynamic 0 disable surface dlighting,
	// matching the old CPU overlay path's behavior.
	if (cvr.flashblend.value !== 0 || cvr.dynamic.value === 0) {
		state.numShaderDlights = 0;
	} else {
		state.numShaderDlights = state.numActiveDlights;
		for (i = 0; i < state.numActiveDlights; ++i) {
			var dl = cl.state.dlights[state.activeDlights[i]];
			var o = i * 4;
			state.dlightPosRadius[o] = dl.origin[0];
			state.dlightPosRadius[o + 1] = dl.origin[1];
			state.dlightPosRadius[o + 2] = dl.origin[2];
			state.dlightPosRadius[o + 3] = dl.radius;
			state.dlightColor[o] = dl.color[0];
			state.dlightColor[o + 1] = dl.color[1];
			state.dlightColor[o + 2] = dl.color[2];
			state.dlightColor[o + 3] = dl.minlight;
		}
	}
};

export const recursiveLightPoint = function (node: Node, start: V3, end: V3, cache: LightCache): 0 | -1 | 1 {
	if (node.contents < 0)
		return -1;

	var normal = node.plane.normal;
	var front = start[0] * normal[0] + start[1] * normal[1] + start[2] * normal[2] - node.plane.dist;
	var back = end[0] * normal[0] + end[1] * normal[1] + end[2] * normal[2] - node.plane.dist;
	var side = front < 0;

	if ((back < 0) === side)
		return recursiveLightPoint(node.children[side === true ? 1 : 0] as Node, start, end, cache);

	var frac = front / (front - back);
	var mid: V3 = vec.scratch();
	mid[0] = start[0] + (end[0] - start[0]) * frac;
	mid[1] = start[1] + (end[1] - start[1]) * frac;
	mid[2] = start[2] + (end[2] - start[2]) * frac;

	var r = recursiveLightPoint(node.children[side === true ? 1 : 0] as Node, start, mid, cache);
	if (r !== 0 && r !== -1)
		return r;

	if ((back < 0) === side)
		return -1;

	var i, surf, tex, s, t, ds, dt;
	for (i = 0; i < node.numfaces; ++i) {
		surf = cl.clState.worldmodel.faces[node.firstface + i];
		if ((surf.sky === true) || (surf.turbulent === true))
			continue;

		if (surf.decoupled) {
			// lmvecs project world->luxels directly (texturemins folded into .w).
			var lv = surf.lmvecs as Float32Array;
			ds = mid[0] * lv[0] + mid[1] * lv[1] + mid[2] * lv[2] + lv[3];
			dt = mid[0] * lv[4] + mid[1] * lv[5] + mid[2] * lv[6] + lv[7];
			if ((ds < 0) || (dt < 0))
				continue;
			if ((ds > surf.lmwidth - 1) || (dt > surf.lmheight - 1))
				continue;
			if (surf.lightofs === 0)
				return 0;
			ds = Math.floor(ds);
			dt = Math.floor(dt);
		} else {
			tex = cl.clState.worldmodel.texinfo[surf.texinfo];

			s = vec.dotProductV3(mid, tex.vecs[0]) + tex.vecs[0][3];
			t = vec.dotProductV3(mid, tex.vecs[1]) + tex.vecs[1][3];
			if ((s < surf.texturemins[0]) || (t < surf.texturemins[1]))
				continue;

			ds = s - surf.texturemins[0];
			dt = t - surf.texturemins[1];
			if ((ds > surf.extents[0]) || (dt > surf.extents[1]))
				continue;

			if (surf.lightofs === 0)
				return 0;

			ds >>= surf.lmshift;
			dt >>= surf.lmshift;
		}

		cache.surf = node.firstface + i + 1;
		cache.ds = ds;
		cache.dt = dt;
		return 1;
	}
	return recursiveLightPoint(node.children[side !== true ? 1 : 0] as Node, mid, end, cache);
};

export const sampleLightmap = function (surfIdx: number, ds: number, dt: number, out: Color): Color {
	var surf = cl.clState.worldmodel.faces[surfIdx - 1];
	var w = surf.decoupled ? surf.lmwidth : (surf.extents[0] >> surf.lmshift) + 1;
	var h = surf.decoupled ? surf.lmheight : (surf.extents[1] >> surf.lmshift) + 1;
	var lightmap = surf.lightofs + (dt * w + ds) * 3;
	var size = w * h * 3;

	out[0] = 0; out[1] = 0; out[2] = 0;
	for (var maps = 0; maps < surf.styles.length; ++maps)
	{
		out[0] += cl.clState.worldmodel.lightdata[lightmap] * lm.state.lightstylevalue[surf.styles[maps]];
		out[1] += cl.clState.worldmodel.lightdata[lightmap + 1] * lm.state.lightstylevalue[surf.styles[maps]];
		out[2] += cl.clState.worldmodel.lightdata[lightmap + 2] * lm.state.lightstylevalue[surf.styles[maps]];
		lightmap += size;
	}

	out[0] = out[0] >> 8;
	out[1] = out[1] >> 8;
	out[2] = out[2] >> 8;
	return out;
};

export const lightPoint = function (p: V3, cache: LightCache): Color {
	if (cl.clState.worldmodel.lightdata == null){
		const out = vec.scratch(); out[0] = 255; out[1] = 255; out[2] = 255;
		return out as Color;
	}

	if (cache.surf === 0 || Math.abs(cache.pos[0] - p[0]) >= 1 || Math.abs(cache.pos[1] - p[1]) >= 1 || Math.abs(cache.pos[2] - p[2]) >= 1) {
		cache.pos[0] = p[0]; cache.pos[1] = p[1]; cache.pos[2] = p[2];
		cache.surf = 0;

		const end = vec.scratch();
		end[0] = p[0]; end[1] = p[1]; end[2] = p[2] - 2048.0;
		const r = recursiveLightPoint(cl.clState.worldmodel.nodes[0] as Node, p, end, cache);

		if (r !== 1)
			cache.surf = -1;
	}

	if (cache.surf > 0)
		return sampleLightmap(cache.surf, cache.ds, cache.dt, vec.scratch() as Color);

	const out = vec.scratch(); out[0] = 0; out[1] = 0; out[2] = 0;
	return out as Color;
};

// const cullBox = function(mins, maxs)
// {
// 	if (vec.boxOnPlaneSide(mins, maxs, state.frustum[0]) === 2)
// 		return true;
// 	if (vec.boxOnPlaneSide(mins, maxs, state.frustum[1]) === 2)
// 		return true;
// 	if (vec.boxOnPlaneSide(mins, maxs, state.frustum[2]) === 2)
// 		return true;
// 	if (vec.boxOnPlaneSide(mins, maxs, state.frustum[3]) === 2)
// 		return true;
// };

export const cullBox = function (emins: V3, emaxs: V3) {
	for (var i = 0; i < 4; i++) {
		var o = i * 4;
		var nx = state.frustumFlat[o], ny = state.frustumFlat[o + 1], nz = state.frustumFlat[o + 2], dist = state.frustumFlat[o + 3];
		var sb = state.frustumSignbits[i];
		var px = (sb & 1) !== 0 ? emins[0] : emaxs[0];
		var py = (sb & 2) !== 0 ? emins[1] : emaxs[1];
		var pz = (sb & 4) !== 0 ? emins[2] : emaxs[2];
		if (nx * px + ny * py + nz * pz < dist)
			return true;
	}
	return false;
};

export const drawSpriteModel = function (e: Entity) {
	var program = GL.useProgram('Sprite', true);
	var num = e.frame;
	if ((num >= e.model.numframes) || (num < 0)) {
		con.dPrint('R.DrawSpriteModel: no such frame ' + num + '\n');
		num = 0;
	}
	var frame = e.model.frames[num] as SpriteFrame | SpriteFrameGroup;
	if (frame.group === true) {
		var fullinterval, targettime, i, time = cl.clState.time + e.syncbase;
		num = frame.frames.length - 1;
		fullinterval = frame.frames[num].interval;
		targettime = time - Math.floor(time / fullinterval) * fullinterval;
		for (i = 0; i < num; ++i) {
			if (frame.frames[i].interval > targettime)
				break;
		}
		frame = frame.frames[i];
	}

	tx.bind(program.textures.tTexture, frame.texturenum, true);
	var r = vec.scratch(), u = vec.scratch()
	if (e.model.oriented === true) {
		vec.angleVectors(e.angles, null, r, u);
	}
	else {
		r = state.vright;
		u = state.vup;
	}
	var p = e.origin;
	// entity .scale grows the sprite quad about its origin, matching Ironwail/QSS r_sprite.c
	// (VectorMA of frame extents * ENTSCALE_DECODE(e->scale)).
	var ss = pr.decodeScale(e.scale);
	var x1 = frame.origin[0] * ss, y1 = frame.origin[1] * ss, x2 = x1 + frame.width * ss, y2 = y1 + frame.height * ss;

	GL.streamGetSpace(6);
	GL.streamWriteFloat3(
		p[0] + x1 * r[0] + y1 * u[0],
		p[1] + x1 * r[1] + y1 * u[1],
		p[2] + x1 * r[2] + y1 * u[2]);
	GL.streamWriteFloat2(0.0, 1.0);
	GL.streamWriteFloat3(
		p[0] + x1 * r[0] + y2 * u[0],
		p[1] + x1 * r[1] + y2 * u[1],
		p[2] + x1 * r[2] + y2 * u[2]);
	GL.streamWriteFloat2(0.0, 0.0);
	GL.streamWriteFloat3(
		p[0] + x2 * r[0] + y1 * u[0],
		p[1] + x2 * r[1] + y1 * u[1],
		p[2] + x2 * r[2] + y1 * u[2]);
	GL.streamWriteFloat2(1.0, 1.0);
	GL.streamWriteFloat3(
		p[0] + x2 * r[0] + y1 * u[0],
		p[1] + x2 * r[1] + y1 * u[1],
		p[2] + x2 * r[2] + y1 * u[2]);
	GL.streamWriteFloat2(1.0, 1.0);
	GL.streamWriteFloat3(
		p[0] + x1 * r[0] + y2 * u[0],
		p[1] + x1 * r[1] + y2 * u[1],
		p[2] + x1 * r[2] + y2 * u[2]);
	GL.streamWriteFloat2(0.0, 0.0);
	GL.streamWriteFloat3(
		p[0] + x2 * r[0] + y2 * u[0],
		p[1] + x2 * r[1] + y2 * u[1],
		p[2] + x2 * r[2] + y2 * u[2]);
	GL.streamWriteFloat2(1.0, 0.0);
};

state.avertexnormals = [
	[-0.525731, 0.0, 0.850651],
	[-0.442863, 0.238856, 0.864188],
	[-0.295242, 0.0, 0.955423],
	[-0.309017, 0.5, 0.809017],
	[-0.16246, 0.262866, 0.951056],
	[0.0, 0.0, 1.0],
	[0.0, 0.850651, 0.525731],
	[-0.147621, 0.716567, 0.681718],
	[0.147621, 0.716567, 0.681718],
	[0.0, 0.525731, 0.850651],
	[0.309017, 0.5, 0.809017],
	[0.525731, 0.0, 0.850651],
	[0.295242, 0.0, 0.955423],
	[0.442863, 0.238856, 0.864188],
	[0.16246, 0.262866, 0.951056],
	[-0.681718, 0.147621, 0.716567],
	[-0.809017, 0.309017, 0.5],
	[-0.587785, 0.425325, 0.688191],
	[-0.850651, 0.525731, 0.0],
	[-0.864188, 0.442863, 0.238856],
	[-0.716567, 0.681718, 0.147621],
	[-0.688191, 0.587785, 0.425325],
	[-0.5, 0.809017, 0.309017],
	[-0.238856, 0.864188, 0.442863],
	[-0.425325, 0.688191, 0.587785],
	[-0.716567, 0.681718, -0.147621],
	[-0.5, 0.809017, -0.309017],
	[-0.525731, 0.850651, 0.0],
	[0.0, 0.850651, -0.525731],
	[-0.238856, 0.864188, -0.442863],
	[0.0, 0.955423, -0.295242],
	[-0.262866, 0.951056, -0.16246],
	[0.0, 1.0, 0.0],
	[0.0, 0.955423, 0.295242],
	[-0.262866, 0.951056, 0.16246],
	[0.238856, 0.864188, 0.442863],
	[0.262866, 0.951056, 0.16246],
	[0.5, 0.809017, 0.309017],
	[0.238856, 0.864188, -0.442863],
	[0.262866, 0.951056, -0.16246],
	[0.5, 0.809017, -0.309017],
	[0.850651, 0.525731, 0.0],
	[0.716567, 0.681718, 0.147621],
	[0.716567, 0.681718, -0.147621],
	[0.525731, 0.850651, 0.0],
	[0.425325, 0.688191, 0.587785],
	[0.864188, 0.442863, 0.238856],
	[0.688191, 0.587785, 0.425325],
	[0.809017, 0.309017, 0.5],
	[0.681718, 0.147621, 0.716567],
	[0.587785, 0.425325, 0.688191],
	[0.955423, 0.295242, 0.0],
	[1.0, 0.0, 0.0],
	[0.951056, 0.16246, 0.262866],
	[0.850651, -0.525731, 0.0],
	[0.955423, -0.295242, 0.0],
	[0.864188, -0.442863, 0.238856],
	[0.951056, -0.16246, 0.262866],
	[0.809017, -0.309017, 0.5],
	[0.681718, -0.147621, 0.716567],
	[0.850651, 0.0, 0.525731],
	[0.864188, 0.442863, -0.238856],
	[0.809017, 0.309017, -0.5],
	[0.951056, 0.16246, -0.262866],
	[0.525731, 0.0, -0.850651],
	[0.681718, 0.147621, -0.716567],
	[0.681718, -0.147621, -0.716567],
	[0.850651, 0.0, -0.525731],
	[0.809017, -0.309017, -0.5],
	[0.864188, -0.442863, -0.238856],
	[0.951056, -0.16246, -0.262866],
	[0.147621, 0.716567, -0.681718],
	[0.309017, 0.5, -0.809017],
	[0.425325, 0.688191, -0.587785],
	[0.442863, 0.238856, -0.864188],
	[0.587785, 0.425325, -0.688191],
	[0.688191, 0.587785, -0.425325],
	[-0.147621, 0.716567, -0.681718],
	[-0.309017, 0.5, -0.809017],
	[0.0, 0.525731, -0.850651],
	[-0.525731, 0.0, -0.850651],
	[-0.442863, 0.238856, -0.864188],
	[-0.295242, 0.0, -0.955423],
	[-0.16246, 0.262866, -0.951056],
	[0.0, 0.0, -1.0],
	[0.295242, 0.0, -0.955423],
	[0.16246, 0.262866, -0.951056],
	[-0.442863, -0.238856, -0.864188],
	[-0.309017, -0.5, -0.809017],
	[-0.16246, -0.262866, -0.951056],
	[0.0, -0.850651, -0.525731],
	[-0.147621, -0.716567, -0.681718],
	[0.147621, -0.716567, -0.681718],
	[0.0, -0.525731, -0.850651],
	[0.309017, -0.5, -0.809017],
	[0.442863, -0.238856, -0.864188],
	[0.16246, -0.262866, -0.951056],
	[0.238856, -0.864188, -0.442863],
	[0.5, -0.809017, -0.309017],
	[0.425325, -0.688191, -0.587785],
	[0.716567, -0.681718, -0.147621],
	[0.688191, -0.587785, -0.425325],
	[0.587785, -0.425325, -0.688191],
	[0.0, -0.955423, -0.295242],
	[0.0, -1.0, 0.0],
	[0.262866, -0.951056, -0.16246],
	[0.0, -0.850651, 0.525731],
	[0.0, -0.955423, 0.295242],
	[0.238856, -0.864188, 0.442863],
	[0.262866, -0.951056, 0.16246],
	[0.5, -0.809017, 0.309017],
	[0.716567, -0.681718, 0.147621],
	[0.525731, -0.850651, 0.0],
	[-0.238856, -0.864188, -0.442863],
	[-0.5, -0.809017, -0.309017],
	[-0.262866, -0.951056, -0.16246],
	[-0.850651, -0.525731, 0.0],
	[-0.716567, -0.681718, -0.147621],
	[-0.716567, -0.681718, 0.147621],
	[-0.525731, -0.850651, 0.0],
	[-0.5, -0.809017, 0.309017],
	[-0.238856, -0.864188, 0.442863],
	[-0.262866, -0.951056, 0.16246],
	[-0.864188, -0.442863, 0.238856],
	[-0.809017, -0.309017, 0.5],
	[-0.688191, -0.587785, 0.425325],
	[-0.681718, -0.147621, 0.716567],
	[-0.442863, -0.238856, 0.864188],
	[-0.587785, -0.425325, 0.688191],
	[-0.309017, -0.5, 0.809017],
	[-0.147621, -0.716567, 0.681718],
	[-0.425325, -0.688191, 0.587785],
	[-0.16246, -0.262866, 0.951056],
	[0.442863, -0.238856, 0.864188],
	[0.16246, -0.262866, 0.951056],
	[0.309017, -0.5, 0.809017],
	[0.147621, -0.716567, 0.681718],
	[0.0, -0.525731, 0.850651],
	[0.425325, -0.688191, 0.587785],
	[0.587785, -0.425325, 0.688191],
	[0.688191, -0.587785, 0.425325],
	[-0.955423, 0.295242, 0.0],
	[-0.951056, 0.16246, 0.262866],
	[-1.0, 0.0, 0.0],
	[-0.850651, 0.0, 0.525731],
	[-0.955423, -0.295242, 0.0],
	[-0.951056, -0.16246, 0.262866],
	[-0.864188, 0.442863, -0.238856],
	[-0.951056, 0.16246, -0.262866],
	[-0.809017, 0.309017, -0.5],
	[-0.864188, -0.442863, -0.238856],
	[-0.951056, -0.16246, -0.262866],
	[-0.809017, -0.309017, -0.5],
	[-0.681718, 0.147621, -0.716567],
	[-0.681718, -0.147621, -0.716567],
	[-0.850651, 0.0, -0.525731],
	[-0.688191, 0.587785, -0.425325],
	[-0.587785, 0.425325, -0.688191],
	[-0.425325, 0.688191, -0.587785],
	[-0.425325, -0.688191, -0.587785],
	[-0.587785, -0.425325, -0.688191],
	[-0.688191, -0.587785, -0.425325]
];

// Persistent, load-time constant — never mutated.
const negX: V3 = [-1.0, 0.0, 0.0];

const clamp = (min: number, v: number, max: number) => v < min ? min : (v > max ? max : v)

// Port of Ironwail R_SetupAliasFrame (r_alias.c:84-149): resolves this draw's pose
// via the same frame/framegroup pick logic as before, updates the entity's pose-lerp
// bookkeeping, and writes the resulting blend into state.aliasLerp.
const setupAliasFrame = function (e: Entity, clmodel: Model) {
	var num = e.frame, i, fullinterval, targettime;
	if ((num >= clmodel.numframes) || (num < 0)) {
		con.dPrint('R.DrawAliasModel: no such frame ' + num + '\n');
		num = 0;
	}
	var time = cl.clState.time + e.syncbase;
	var frame = clmodel.frames[num] as AliasFrame | AliasFrameGroup;
	var isGroup = frame.group === true;
	if (isGroup) {
		var group = frame as AliasFrameGroup;
		num = group.frames.length - 1;
		fullinterval = group.frames[num].interval;
		targettime = time - Math.floor(time / fullinterval) * fullinterval;
		for (i = 0; i < num; ++i) {
			if (group.frames[i].interval > targettime)
				break;
		}
		frame = group.frames[i];
		e.lerptime = group.frames[0].interval;
	}
	else
		e.lerptime = 0.1;
	var posenum = (frame as AliasFrame).cmdofs;

	if ((e.lerpflags & LERP.resetanim) !== 0) { // kill any lerp in progress
		e.lerpstart = 0;
		e.previouspose = posenum;
		e.currentpose = posenum;
		e.lerpflags &= ~LERP.resetanim;
	}
	else if (e.currentpose !== posenum) { // pose changed, start new lerp
		if ((e.lerpflags & LERP.resetanim2) !== 0) { // defer lerping one more time
			e.lerpstart = 0;
			e.previouspose = posenum;
			e.currentpose = posenum;
			e.lerpflags &= ~LERP.resetanim2;
		}
		else {
			e.lerpstart = cl.clState.time;
			e.previouspose = e.currentpose;
			e.currentpose = posenum;
		}
	}

	if (e.previouspose < 0) // never-drawn entity that skipped the resetanim paths
		e.previouspose = e.currentpose;

	var lerp = state.aliasLerp;
	if (cvr.lerpmodels.value !== 0 && !(clmodel.nolerp && cvr.lerpmodels.value !== 2)) {
		if ((e.lerpflags & LERP.finish) !== 0 && !isGroup)
			lerp.blend = clamp(0, (cl.clState.time - e.lerpstart) / (e.lerpfinish - e.lerpstart), 1);
		else
			lerp.blend = clamp(0, (cl.clState.time - e.lerpstart) / e.lerptime, 1);
		if (lerp.blend === 1)
			e.previouspose = e.currentpose;
		lerp.pose1ofs = e.previouspose;
		lerp.pose2ofs = e.currentpose;
	}
	else { // don't lerp
		lerp.blend = 1;
		lerp.pose1ofs = posenum;
		lerp.pose2ofs = posenum;
	}
};

// Port of Ironwail R_SetupEntityTransform (r_alias.c:156-211): resolves the
// MOVETYPE_STEP origin/angle lerp state and writes the blended transform into
// originOut/anglesOut (vec.ts out-param convention).
const setupEntityTransform = function (e: Entity, originOut: V3, anglesOut: V3): V3 {
	if ((e.lerpflags & LERP.resetmove) !== 0) { // kill any lerps in progress
		e.movelerpstart = 0;
		vec.copy(e.origin, e.previousorigin);
		vec.copy(e.origin, e.currentorigin);
		vec.copy(e.angles, e.previousangles);
		vec.copy(e.angles, e.currentangles);
		e.lerpflags &= ~LERP.resetmove;
	}
	else if (e.origin[0] !== e.currentorigin[0] || e.origin[1] !== e.currentorigin[1] || e.origin[2] !== e.currentorigin[2] ||
		e.angles[0] !== e.currentangles[0] || e.angles[1] !== e.currentangles[1] || e.angles[2] !== e.currentangles[2]) { // origin/angles changed, start new lerp
		e.movelerpstart = cl.clState.time;
		vec.copy(e.currentorigin, e.previousorigin);
		vec.copy(e.origin, e.currentorigin);
		vec.copy(e.currentangles, e.previousangles);
		vec.copy(e.angles, e.currentangles);
	}

	if (cvr.lerpmove.value !== 0 && e !== cl.clState.viewent && (e.lerpflags & LERP.movestep) !== 0) {
		var blend: number;
		if ((e.lerpflags & LERP.finish) !== 0)
			blend = clamp(0, (cl.clState.time - e.movelerpstart) / (e.lerpfinish - e.movelerpstart), 1);
		else
			blend = clamp(0, (cl.clState.time - e.movelerpstart) / 0.1, 1);

		originOut[0] = e.previousorigin[0] + (e.currentorigin[0] - e.previousorigin[0]) * blend;
		originOut[1] = e.previousorigin[1] + (e.currentorigin[1] - e.previousorigin[1]) * blend;
		originOut[2] = e.previousorigin[2] + (e.currentorigin[2] - e.previousorigin[2]) * blend;

		var d;
		d = e.currentangles[0] - e.previousangles[0];
		if (d > 180) d -= 360; else if (d < -180) d += 360;
		anglesOut[0] = e.previousangles[0] + d * blend;

		d = e.currentangles[1] - e.previousangles[1];
		if (d > 180) d -= 360; else if (d < -180) d += 360;
		anglesOut[1] = e.previousangles[1] + d * blend;

		d = e.currentangles[2] - e.previousangles[2];
		if (d > 180) d -= 360; else if (d < -180) d += 360;
		anglesOut[2] = e.previousangles[2] + d * blend;
	}
	else { // don't lerp
		vec.copy(e.origin, originOut);
		vec.copy(e.angles, anglesOut);
	}
	return originOut;
};

export const drawAliasModel = function (e: Entity) {
	const gl = GL.getContext()
	var clmodel = e.model;

	// entity .scale (16 = 1.0) grows the cull sphere too, or big-scaled models pop at the
	// screen edge -- Ironwail R_GetEntityBounds scales bounds by ENTSCALE_DECODE(e->scale).
	var scalefactor = pr.decodeScale(e.scale);
	var cullRadius = clmodel.boundingradius * scalefactor;
	var cullMins = state.cullMins, cullMaxs = state.cullMaxs;
	cullMins[0] = e.origin[0] - cullRadius;
	cullMins[1] = e.origin[1] - cullRadius;
	cullMins[2] = e.origin[2] - cullRadius;
	cullMaxs[0] = e.origin[0] + cullRadius;
	cullMaxs[1] = e.origin[1] + cullRadius;
	cullMaxs[2] = e.origin[2] + cullRadius;
	if (cullBox(cullMins, cullMaxs) === true)
		return;

	// Culling above stays on e.origin (un-lerped) per Ironwail; only uniforms below use
	// the movestep-lerped transform.
	var lerpOrigin: V3 = vec.scratch(), lerpAngles: V3 = vec.scratch();
	setupEntityTransform(e, lerpOrigin, lerpAngles);

	var program;
	if ((e.colormap !== 0) && (clmodel.player === true) && (cvr.nocolors.value === 0)) {
		program = GL.useProgram('Player');
		var top = (cl.clState.scores[e.colormap - 1].colors & 0xf0) + 4;
		var bottom = ((cl.clState.scores[e.colormap - 1].colors & 0xf) << 4) + 4;
		if (top <= 127)
			top += 7;
		if (bottom <= 127)
			bottom += 7;
		top = vid.d_8to24table[top];
		bottom = vid.d_8to24table[bottom];
		gl.uniform3f(program.uniforms.uTop, top & 0xff, (top >> 8) & 0xff, top >> 16);
		gl.uniform3f(program.uniforms.uBottom, bottom & 0xff, (bottom >> 8) & 0xff, bottom >> 16);
	}
	else
		program = GL.useProgram('Alias');

	var entalpha = pr.decodeAlpha(e.alpha);
	if (entalpha === 0)
		return;
	gl.uniform1f(program.uniforms.uAlpha, entalpha);
	if (entalpha < 1) {
		gl.depthMask(false);
		gl.enable(gl.BLEND);
		gl.blendFunc(gl.SRC_ALPHA, gl.ONE_MINUS_SRC_ALPHA);
	}

	gl.uniform3fv(program.uniforms.uOrigin, lerpOrigin);
	gl.uniformMatrix3fv(program.uniforms.uAngles, false, GL.rotationMatrix(lerpAngles[0], lerpAngles[1], lerpAngles[2], scalefactor));

	var ambientlight = lightPoint(e.origin, e.lightcache);

	if (e === cl.clState.viewent)  {
		add = 72 - (ambientlight[0] + ambientlight[1] + ambientlight[2])
		if (add > 0) {
			ambientlight[0] += add / 3
			ambientlight[1] += add / 3
			ambientlight[2] += add / 3
		}
	}
	var i, dl, add;
	var dx, dy, dz, distSq;
	for (i = 0; i < state.numActiveDlights; ++i) {
		dl = cl.state.dlights[state.activeDlights[i]];
		dx = e.origin[0] - dl.origin[0];
		dy = e.origin[1] - dl.origin[1];
		dz = e.origin[2] - dl.origin[2];
		distSq = dx * dx + dy * dy + dz * dz;
		if (dl.radius * dl.radius <= distSq)
			continue;
		add = dl.radius - Math.sqrt(distSq);
		vec.vectorMA(ambientlight, add, dl.color, ambientlight)
	}

	var shadelight: Color = vec.scratch() as unknown as Color
	vec.copy(ambientlight, shadelight)

	ambientlight[0] = ambientlight[0] > 128.0 ? 128.0 : ambientlight[0]
	ambientlight[1] = ambientlight[1] > 128.0 ? 128.0 : ambientlight[1]
	ambientlight[2] = ambientlight[2] > 128.0 ? 128.0 : ambientlight[2]

	shadelight[0] = ambientlight[0] + shadelight[0] > 192.0 ? 192.0 - ambientlight[0] : shadelight[0]
	shadelight[1] = ambientlight[1] + shadelight[1] > 192.0 ? 192.0 - ambientlight[1] : shadelight[1]
	shadelight[2] = ambientlight[2] + shadelight[2] > 192.0 ? 192.0 - ambientlight[2] : shadelight[2]

	// minimum light value on players (8)
	if ((e.num >= 1) && (e.num <= cl.clState.maxclients)) {
		add = 24.0 - (ambientlight[0] + ambientlight[1] + ambientlight[2]);
		if (add > 0.0) {
			ambientlight[0] += add / 3.0
			ambientlight[1] += add / 3.0
			ambientlight[2] += add / 3.0
			vec.copy(ambientlight, shadelight)
		}
	}
	gl.uniform3fv(program.uniforms.uAmbientLight, vec.scale(ambientlight, 0.0078125, vec.scratch()));
	gl.uniform3fv(program.uniforms.uShadeLight, vec.scale(shadelight, 0.0078125, vec.scratch()));
	gl.uniform1i(program.uniforms.uUseOverbright, cvr.overbright.value);
	gl.uniform1i(program.uniforms.uUseFullbrights, cvr.fullbrights.value);

	var forward:V3 = vec.scratch(), right:V3 = vec.scratch(), up:V3 = vec.scratch();
	vec.angleVectors(lerpAngles, forward, right, up);
	var lightVec = vec.scratch();
	lightVec[0] = vec.dotProductV3(negX, forward);
	lightVec[1] = -vec.dotProductV3(negX, right);
	lightVec[2] = vec.dotProductV3(negX, up);
	gl.uniform3fv(program.uniforms.uLightVec, lightVec);

	state.c_alias_polys += clmodel.numtris;

	setupAliasFrame(e, clmodel);
	var lerp = state.aliasLerp;
	gl.bindBuffer(gl.ARRAY_BUFFER, clmodel.cmds);
	gl.vertexAttribPointer(program.attributeMap.aPosition.location, 3, gl.FLOAT, false, 24, lerp.pose1ofs);
	gl.vertexAttribPointer(program.attributeMap.aNormal.location, 3, gl.FLOAT, false, 24, lerp.pose1ofs + 12);
	gl.vertexAttribPointer(program.attributeMap.aPosition2.location, 3, gl.FLOAT, false, 24, lerp.pose2ofs);
	gl.vertexAttribPointer(program.attributeMap.aNormal2.location, 3, gl.FLOAT, false, 24, lerp.pose2ofs + 12);
	gl.vertexAttribPointer(program.attributeMap.aTexCoord.location, 2, gl.FLOAT, false, 0, 0);
	gl.uniform1f(program.uniforms.uBlend, lerp.blend);

	if (clmodel.surfaces !== undefined) {
		// md3: each surface has its own external skin(s) and vertex range in the shared
		// pose VBO; draw them one at a time (drawArrays first/count offsets every attribute
		// uniformly, so the texcoord/pose pointers set above stay correct per surface).
		var sf, ssi;
		for (var s = 0; s < clmodel.surfaces.length; ++s) {
			sf = clmodel.surfaces[s];
			ssi = e.skinnum;
			if ((ssi < 0) || (ssi >= sf.skins.length))
				ssi = 0;
			tx.bind(program.textures.tTexture, sf.skins[ssi].texnum);
			gl.drawArrays(gl.TRIANGLES, sf.first, sf.count);
		}
	}
	else {
		var num, fullinterval, targettime, i;
		var time = cl.clState.time + e.syncbase;
		num = e.skinnum;
		if ((num >= clmodel.numskins) || (num < 0)) {
			con.dPrint('R.DrawAliasModel: no such skin # ' + num + '\n');
			num = 0;
		}
		var skin = clmodel.skins[num];
		if (skin.group === true) {
			num = skin.skins.length - 1;
			fullinterval = skin.skins[num].interval;
			targettime = time - Math.floor(time / fullinterval) * fullinterval;
			for (i = 0; i < num; ++i) {
				if (skin.skins[i].interval > targettime)
					break;
			}
			skin = skin.skins[i];
		}
		tx.bind(program.textures.tTexture, skin.texturenum.texnum);
		if ((e.colormap !== 0) && (clmodel.player === true) && (cvr.nocolors.value === 0))
			tx.bind(program.textures.tPlayer, skin.playertexture);

		gl.drawArrays(gl.TRIANGLES, 0, clmodel.numtris * 3);
	}

	if (entalpha < 1) {
		gl.disable(gl.BLEND);
		gl.depthMask(true);
	}
};

export const drawEntitiesOnList = function (alphaPass: boolean) {
	const gl = GL.getContext()

	if (cvr.drawentities.value === 0)
		return;
	var i, ent, entalpha
	for (i = 0; i < cl.state.numvisedicts; ++i) {
		ent = cl.state.visedicts[i];
		entalpha = pr.decodeAlpha(ent.alpha);
		// johnfitz -- opaque entities in the first pass, translucent in the alpha pass
		if (ent.model == null || (entalpha === 1) === alphaPass)
			continue;
		switch (ent.model.type) {
			case mod.TYPE.alias:
				drawAliasModel(ent);
				continue;
			case mod.TYPE.brush:
				drawBrushModel(ent);
		}
	}

	if (!alphaPass) {
		GL.streamFlush();
		gl.depthMask(false);
		gl.enable(gl.BLEND);
		for (i = 0; i < cl.state.numvisedicts; ++i) {
			ent = cl.state.visedicts[i];
			if (ent.model == null)
				continue;
			if (ent.model.type === mod.TYPE.sprite)
				drawSpriteModel(ent);
		}
		GL.streamFlush();
		gl.disable(gl.BLEND);
		gl.depthMask(true);
	}
};

export const drawViewModel = function () {
	const gl = GL.getContext()
	if (sky.state.skyroom_drawing)
		return; // no viewmodel inside the skyroom (QSS r_alias.c:1124)
	if (cvr.drawviewmodel.value === 0)
		return;
	if (chase.cvr.active.value !== 0)
		return;
	if (cvr.drawentities.value === 0)
		return;
	if ((cl.clState.items & def.IT.invisibility) !== 0)
		return;
	if (cl.clState.stats[def.STAT.health] <= 0)
		return;
	if (cl.clState.viewent.model == null)
		return;

	gl.depthRange(0.0, 0.3);

	var ymax = 4.0 * Math.tan(scr.cvr.fov.value * 0.82 * Math.PI / 360.0);
	state.perspective[0] = 4.0 / (ymax * state.refdef.vrect.width / state.refdef.vrect.height);
	state.perspective[5] = 4.0 / ymax;
	var program = GL.useProgram('Alias');
	gl.uniformMatrix4fv(program.uniforms.uPerspective, false, state.perspective);

	drawAliasModel(cl.clState.viewent);

	ymax = 4.0 * Math.tan(state.refdef.fov_y * Math.PI / 360.0);
	state.perspective[0] = 4.0 / (ymax * state.refdef.vrect.width / state.refdef.vrect.height);
	state.perspective[5] = 4.0 / ymax;
	program = GL.useProgram('Alias');
	gl.uniformMatrix4fv(program.uniforms.uPerspective, false, state.perspective);

	gl.depthRange(0.0, 1.0);
};

export const polyBlend = function () {
	if (cvr.polyblend.value === 0)
		return;
	if (v.blend[3] === 0.0)
		return;
	GL.useProgram('Fill', true);
	var vrect = state.refdef.vrect;
	GL.streamDrawColoredQuad(vrect.x, vrect.y, vrect.width, vrect.height,
		v.blend[0], v.blend[1], v.blend[2], v.blend[3] * 255.0);
};

export const setFrustum = function () {
	vec.rotatePointAroundVector(state.vup, state.vpn, -(90.0 - state.refdef.fov_x * 0.5), state.frustum[0].normal);
	vec.rotatePointAroundVector(state.vup, state.vpn, 90.0 - state.refdef.fov_x * 0.5, state.frustum[1].normal);
	vec.rotatePointAroundVector(state.vright, state.vpn, 90.0 - state.refdef.fov_y * 0.5, state.frustum[2].normal);
	// the fourth plane was missing since the original port -- everything below/above (per
	// sign) was never culled, which also masked bad model radii on that edge
	vec.rotatePointAroundVector(state.vright, state.vpn, -(90.0 - state.refdef.fov_y * 0.5), state.frustum[3].normal);

	var i, out;
	for (i = 0; i <= 3; ++i) {
		out = state.frustum[i];
		out.type = 5;
		out.dist = vec.dotProductV3(state.refdef.vieworg, out.normal);
		out.signbits = 0;
		if (out.normal[0] < 0.0)
			out.signbits = 1;
		if (out.normal[1] < 0.0)
			out.signbits += 2;
		if (out.normal[2] < 0.0)
			out.signbits += 4;

		state.frustumFlat[i * 4] = out.normal[0];
		state.frustumFlat[i * 4 + 1] = out.normal[1];
		state.frustumFlat[i * 4 + 2] = out.normal[2];
		state.frustumFlat[i * 4 + 3] = out.dist;
		state.frustumSignbits[i] = out.signbits;
	}
};


export const perspective = function () {
	const gl = GL.getContext()
	var viewangles = state.viewAnglesRad;
	viewangles[0] = state.refdef.viewangles[0] * Math.PI / 180.0;
	viewangles[1] = (state.refdef.viewangles[1] - 90.0) * Math.PI / -180.0;
	viewangles[2] = state.refdef.viewangles[2] * Math.PI / -180.0;
	var sp = Math.sin(viewangles[0]);
	var cp = Math.cos(viewangles[0]);
	var sy = Math.sin(viewangles[1]);
	var cy = Math.cos(viewangles[1]);
	var sr = Math.sin(viewangles[2]);
	var cr = Math.cos(viewangles[2]);
	var viewMatrix = state.viewMatrix;
	viewMatrix[0] = cr * cy + sr * sp * sy; viewMatrix[1] = cp * sy; viewMatrix[2] = -sr * cy + cr * sp * sy;
	viewMatrix[3] = cr * -sy + sr * sp * cy; viewMatrix[4] = cp * cy; viewMatrix[5] = -sr * -sy + cr * sp * cy;
	viewMatrix[6] = sr * cp; viewMatrix[7] = -sp; viewMatrix[8] = cr * cp;

	if (v.cvr.gamma.value < 0.2)
		cvar.setValue('gamma', 0.2);
	else if (v.cvr.gamma.value > 1.0)
		cvar.setValue('gamma', 1.0);

	GL.unbindProgram();
	var i, program;
	for (i = 0; i < GL.state.programs.length; ++i) {
		program = GL.state.programs[i];
		gl.useProgram(program.program);
		if (program.uniforms.uViewOrigin != null)
			gl.uniform3fv(program.uniforms.uViewOrigin, state.refdef.vieworg);
		if (program.uniforms.uViewAngles != null)
			gl.uniformMatrix3fv(program.uniforms.uViewAngles, false, viewMatrix);
		if (program.uniforms.uPerspective != null)
			gl.uniformMatrix4fv(program.uniforms.uPerspective, false, state.perspective);
		if (program.uniforms.uGamma != null)
			gl.uniform1f(program.uniforms.uGamma, v.cvr.gamma.value);
		if (program.uniforms.uVpn != null)
			gl.uniform3fv(program.uniforms.uVpn, state.vpn);
	}
};

export const setupGL = function () {
	const gl = GL.getContext()
	if (state.dowarp === true) {
		gl.bindFramebuffer(gl.FRAMEBUFFER, state.warpbuffer);
		gl.clear(gl.COLOR_BUFFER_BIT + gl.DEPTH_BUFFER_BIT);
		gl.viewport(0, 0, state.warpwidth, state.warpheight);
	}
	else {
		var vrect = state.refdef.vrect;
		var pixelRatio = scr.state.devicePixelRatio;
		gl.viewport((vrect.x * pixelRatio) >> 0, ((vid.state.height - vrect.height - vrect.y) * pixelRatio) >> 0, (vrect.width * pixelRatio) >> 0, (vrect.height * pixelRatio) >> 0);
	}
	perspective();
	gl.enable(gl.DEPTH_TEST);
};

// let array = []
// let lastREport = 0
// const perf = (start, end) => {
// 	let ms = end - start
// 	if(array.length> 50) {
// 		array.pop()
// 	}
// 	array.push(ms)
// 	if (end - lastREport > 1000) {
// 		lastREport = end
// 		console.log('Performance: ' + ((array.reduce((partialSum, a) => partialSum + a, 0)) / array.length).toFixed(2))
// 	}
// }
export const renderScene = function () {
	const gl = GL.getContext()
	animateLight();
	vec.angleVectors(state.refdef.viewangles, state.vpn, state.vright, state.vup);
	state.viewleaf = mod.pointInLeaf(state.refdef.vieworg, cl.clState.worldmodel);
	v.setContentsColor(state.viewleaf.contents);
	v.calcBlend();
	state.dowarp = (cvr.waterwarp.value !== 0) && (state.viewleaf.contents <= mod.CONTENTS.water) && (state.warpSupported !== false);
	setFrustum();
	setupGL();
	var rs_t = performance.now();
	markSurfaces();
	state.rs_markms = performance.now() - rs_t;
	rs_t = performance.now();
	markWorldFrustum();
	state.rs_walkms = performance.now() - rs_t;
	gl.enable(gl.CULL_FACE);
	drawSkyBox();
	drawViewModel();
	drawTextureChains(gl, cl.clState.worldmodel, null, TexChain.world);
	drawEntitiesOnList(false);
	drawTextureChains_litwater(gl, cl.clState.worldmodel, null, TexChain.world);
	drawTextureChains_water(gl, cl.clState.worldmodel, null, TexChain.world);
	drawEntitiesOnList(true);
	gl.disable(gl.CULL_FACE);
	renderDlights();
	runParticles();
	drawParticles();
	pscript.runPScriptParticles();
	pscript.drawPScriptParticles();
};

export const renderView = function () {
	// During a map change mod.clearAll guts the old worldmodel while the client
	// can still be connected for a few frames (async spawnServer yields) — don't
	// walk a gutted world; the loading plaque/console covers the screen.
	if (!cl.clState.worldmodel || (cl.clState.worldmodel as any).nodes == null)
		return;
	const gl = GL.getContext()
	var time1;
	if (cvr.speeds.value !== 0)
		time1 = sys.floatTime();
	state.c_brush_verts = 0;
	state.c_alias_polys = 0;
	state.rs_rebuilds = 0;
	gl.clear(gl.COLOR_BUFFER_BIT + gl.DEPTH_BUFFER_BIT);

	// Skyroom (_skyroom): draw the world once from the skyroom camera into color+depth,
	// then clear only depth so the main pass composites over it through the sky windows.
	// Gated on a sky surface having been visible last frame (QSS gl_rmain.c:1208 R_RenderView).
	sky.state.skyroom_drawn = false;
	if (sky.state.skyroom_enabled && sky.state.skyVisibleLastFrame && cl.clState.worldmodel) {
		vec.copy(state.refdef.vieworg, state.skyroomSaveOrg);
		vec.copy(state.refdef.viewangles, state.skyroomSaveAng);
		// vieworg = skyroom_origin + parallax * mainvieworg (QSS VectorMA, gl_rmain.c:1216);
		// angles unchanged (spin/orientation parsed but not applied — minimal key first).
		var sr = sky.state.skyroom_origin;
		state.refdef.vieworg[0] = sr[0] + sr[3] * state.skyroomSaveOrg[0];
		state.refdef.vieworg[1] = sr[1] + sr[3] * state.skyroomSaveOrg[1];
		state.refdef.vieworg[2] = sr[2] + sr[3] * state.skyroomSaveOrg[2];
		sky.state.skyroom_drawing = true;
		renderScene();
		sky.state.skyroom_drawing = false;
		vec.copy(state.skyroomSaveOrg, state.refdef.vieworg);
		vec.copy(state.skyroomSaveAng, state.refdef.viewangles);
		sky.state.skyroom_drawn = true;
		gl.clear(gl.DEPTH_BUFFER_BIT); // keep skyroom color, reset depth for the main view
	}

	// reset the sky-visible accumulator; drawSkyBox sets it when a sky surface draws
	sky.state.skyVisibleThisFrame = false;
	renderScene();
	// 1-frame-lagged gate for next frame; never render a skyroom from inside the void
	sky.state.skyVisibleLastFrame = state.viewleaf.contents === mod.CONTENTS.solid
		? false : sky.state.skyVisibleThisFrame;
	sky.state.skyroom_drawn = false;
	if (cvr.speeds.value !== 0) {
		var time2 = Math.floor((sys.floatTime() - time1) * 1000.0);
		var c_brush_polys = state.c_brush_verts / 3;
		var c_alias_polys = state.c_alias_polys;
		var message = ((time2 >= 100) ? '' : ((time2 >= 10) ? ' ' : '  ')) + time2 + ' ms  ';
		message += ((c_brush_polys >= 1000) ? '' : ((c_brush_polys >= 100) ? ' ' : ((c_brush_polys >= 10) ? '  ' : '   '))) + c_brush_polys + ' wpoly ';
		message += ((c_alias_polys >= 1000) ? '' : ((c_alias_polys >= 100) ? ' ' : ((c_alias_polys >= 10) ? '  ' : '   '))) + c_alias_polys + ' epoly ';
		message += 'mark ' + state.rs_markms.toFixed(1) + ' walk ' + state.rs_walkms.toFixed(1) + (state.rs_rebuilds ? ' REBUILD' : '') + '\n';
		con.print(message);
	}
};

// misc

export const initTextures = function () {
	const gl = GL.getContext()
	var data = new Uint8Array(new ArrayBuffer(256));
	var i, j;
	for (i = 0; i < 8; ++i) {
		for (j = 0; j < 8; ++j) {
			data[(i << 4) + j] = data[136 + (i << 4) + j] = 255;
			data[8 + (i << 4) + j] = data[128 + (i << 4) + j] = 0;
		}
	}
	state.notexture_mip = tx.createNoTexture(gl)
	tx.bind(0, state.notexture_mip.texturenum);
	tx.upload(data, 16, 16);

	state.solidskytexture = gl.createTexture();
	tx.bind(0, state.solidskytexture);
	gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_MIN_FILTER, gl.LINEAR);
	gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_MAG_FILTER, gl.LINEAR);

	state.alphaskytexture = gl.createTexture();
	tx.bind(0, state.alphaskytexture);
	gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_MIN_FILTER, gl.LINEAR);
	gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_MAG_FILTER, gl.LINEAR);

	state.null_texture = gl.createTexture();
	tx.bind(0, state.null_texture);
	gl.texImage2D(gl.TEXTURE_2D, 0, gl.RGBA, 1, 1, 0, gl.RGBA, gl.UNSIGNED_BYTE, new Uint8Array([0, 0, 0, 0]));
	gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_MIN_FILTER, gl.NEAREST);
	gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_MAG_FILTER, gl.NEAREST);
};

export const init = function () {
	const gl = GL.getContext()
	// fresh GL context (first init or game-view remount): any previously built
	// VBO/lightmap textures belong to the dead context, force a rebuild
	state.builtWorldmodel = null
	batchRender.init(gl)
	initTextures();

	cmd.addCommand('timerefresh', timeRefresh_f);
	cmd.addCommand('pointfile', readPointFile_f);

	cvr.waterwarp = cvar.registerVariable('r_waterwarp', '1');
	cvr.fullbright = cvar.registerVariable('r_fullbright', '0');
	cvr.drawentities = cvar.registerVariable('r_drawentities', '1');
	cvr.drawviewmodel = cvar.registerVariable('r_drawviewmodel', '1');
	cvr.novis = cvar.registerVariable('r_novis', '0');
	cvr.speeds = cvar.registerVariable('r_speeds', '0');
	cvr.polyblend = cvar.registerVariable('gl_polyblend', '1');
	cvr.flashblend = cvar.registerVariable('gl_flashblend', '0');
	cvr.nocolors = cvar.registerVariable('gl_nocolors', '0');
	cvr.overbright = cvar.registerVariable('gl_overbright', '1');
	cvr.fullbrights = cvar.registerVariable('gl_fullbrights', '1');
	cvr.oldskyleaf = cvar.registerVariable('oldskyleaf', '0')
	cvr.flatlightstyles = cvar.registerVariable('r_flatlightstyles', '0')
	cvr.lerplightstyles = cvar.registerVariable('r_lerplightstyles', '1', true)
  cvr.litwater = cvar.registerVariable('r_litwater', '1')
  cvr.wateralpha = cvar.registerVariable('r_wateralpha', '1')
  cvr.lavaalpha = cvar.registerVariable('r_lavaalpha', '0')
  cvr.telealpha = cvar.registerVariable('r_telealpha', '0')
  cvr.slimealpha = cvar.registerVariable('r_slimealpha', '0')
  cvr.dynamic = cvar.registerVariable('r_dynamic', '1')
  cvr.test = cvar.registerVariable('r_test', '0')
  cvr.lerpmodels = cvar.registerVariable('r_lerpmodels', '1')
  cvr.lerpmove = cvar.registerVariable('r_lerpmove', '1')

	cvar.registerChangedEvent('r_novis', () => state.vis_changed = true)
	
	initParticles();
	pscript.init();

	GL.createProgram('Alias',
		['uOrigin', 'uAngles', 'uViewOrigin', 'uViewAngles', 'uPerspective', 'uLightVec', 'uGamma', 'uAmbientLight', 'uShadeLight', 'uUseOverbright', 'uUseFullbrights', 'uBlend', 'uAlpha'],
		[
			createAttribParam('aPosition', gl.FLOAT, 3),
			createAttribParam('aNormal', gl.FLOAT, 3),
			createAttribParam('aTexCoord', gl.FLOAT, 2),
			createAttribParam('aPosition2', gl.FLOAT, 3),
			createAttribParam('aNormal2', gl.FLOAT, 3)
		],
		['tTexture']);

	GL.createProgram(
		'Brush',
		['uUseFullbrightTex', 'uUseOverbright', 'uUseAlphaTest',
			'uAlpha', 'uPerspective', 'uViewAngles', 'uViewOrigin',
			'uOrigin', 'uAngles', 'uFogDensity', 'uFogColor', 'uGamma',
			'uLightStyles[0]', 'uNumDlights', 'uDlightPosRadius[0]', 'uDlightColor[0]',
			'uWarp', 'uTime'],
		[
			createAttribParam('Vert', gl.FLOAT, 3, false),
			createAttribParam('TexCoords', gl.FLOAT, 2, false),
			createAttribParam('LMCoords', gl.FLOAT, 2, false),
			createAttribParam('LMStyles', gl.FLOAT, 4, false),
		],
		['Tex', 'LMTex0', 'FullbrightTex', 'LMTex1', 'LMTex2', 'LMTex3'])
	GL.createProgram('Dlight',
		['uOrigin', 'uViewOrigin', 'uViewAngles', 'uPerspective', 'uRadius', 'uGamma'],
		[
			createAttribParam('aPosition', gl.FLOAT, 3)
		],
		[]);
	GL.createProgram('Player',
		['uOrigin', 'uAngles', 'uViewOrigin', 'uViewAngles', 'uPerspective', 'uLightVec', 'uGamma', 'uAmbientLight', 'uShadeLight', 'uUseOverbright', 'uUseFullbrights', 'uTop', 'uBottom', 'uBlend', 'uAlpha'],
		[
			createAttribParam('aPosition', gl.FLOAT, 3),
			createAttribParam('aNormal', gl.FLOAT, 3),
			createAttribParam('aTexCoord', gl.FLOAT, 2),
			createAttribParam('aPosition2', gl.FLOAT, 3),
			createAttribParam('aNormal2', gl.FLOAT, 3)
		],
		['tTexture', 'tPlayer']);
	GL.createProgram('Sprite',
		['uViewOrigin', 'uViewAngles', 'uPerspective', 'uGamma'],
		[
			createAttribParam('aPosition', gl.FLOAT, 3),
			createAttribParam('aTexCoord', gl.FLOAT, 2)
		],
		['tTexture']);
	GL.createProgram('Turbulent',
		['uOrigin', 'uAngles', 'uViewOrigin', 'uViewAngles', 'uPerspective', 'uGamma', 'uTime', 'uAlpha'],
		[
			createAttribParam('aPosition', gl.FLOAT, 3), 
			createAttribParam('aTexCoord', gl.FLOAT, 2)
		],
		['tTexture']);
	GL.createProgram('Warp',
		['uOrtho', 'uTime'],
		[
			createAttribParam('aPosition', gl.FLOAT, 2),
			createAttribParam('aTexCoord', gl.FLOAT, 2)
		],
		['tTexture']);
	GL.createProgram('PScript',
		['uViewOrigin', 'uViewAngles', 'uPerspective', 'uGamma', 'uVright', 'uVup', 'uPixelWidth'],
		[
			createAttribParam('aCorner', gl.FLOAT, 2),
			createAttribParam('aOrigin', gl.FLOAT, 3),
			createAttribParam('aVelocity', gl.FLOAT, 3),
			createAttribParam('aSize', gl.FLOAT, 1),
			createAttribParam('aRotation', gl.FLOAT, 1),
			createAttribParam('aUV', gl.FLOAT, 4),
			createAttribParam('aOrientation', gl.FLOAT, 1),
			createAttribParam('aColor', gl.UNSIGNED_BYTE, 4, true)
		],
		['tTexture']);

	state.warpbuffer = gl.createFramebuffer();
	state.warptexture = gl.createTexture();
	tx.bind(0, state.warptexture);
	gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_MIN_FILTER, gl.LINEAR);
	gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_MAG_FILTER, gl.LINEAR);
	gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_WRAP_S, gl.CLAMP_TO_EDGE);
	gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_WRAP_T, gl.CLAMP_TO_EDGE);
	// WEBGL_depth_stencil is universally supported on Android (Adreno/Mali) and
	// produces a complete FBO when combined with an RGBA colour texture, whereas
	// DEPTH_COMPONENT16 silently produces an incomplete FBO on many mobile GPUs.
	const depthStencilExt = gl.getExtension('WEBGL_depth_stencil');
	state.warpDepthFormat     = depthStencilExt ? (gl as any).DEPTH_STENCIL           : gl.DEPTH_COMPONENT16;
	state.warpDepthAttachment = depthStencilExt ? (gl as any).DEPTH_STENCIL_ATTACHMENT : gl.DEPTH_ATTACHMENT;
	state.warpSupported = true;
	state.oldwarpwidth = 0;
	state.oldwarpheight = 0;
	state.warprenderbuffer = gl.createRenderbuffer();
	gl.bindRenderbuffer(gl.RENDERBUFFER, state.warprenderbuffer);
	gl.renderbufferStorage(gl.RENDERBUFFER, state.warpDepthFormat, 0, 0);
	gl.bindRenderbuffer(gl.RENDERBUFFER, null);
	gl.bindFramebuffer(gl.FRAMEBUFFER, state.warpbuffer);
	gl.framebufferTexture2D(gl.FRAMEBUFFER, gl.COLOR_ATTACHMENT0, gl.TEXTURE_2D, state.warptexture, 0);
	gl.framebufferRenderbuffer(gl.FRAMEBUFFER, state.warpDepthAttachment, gl.RENDERBUFFER, state.warprenderbuffer);
	gl.bindFramebuffer(gl.FRAMEBUFFER, null);

	state.dlightvecs = gl.createBuffer();
	gl.bindBuffer(gl.ARRAY_BUFFER, state.dlightvecs);
	gl.bufferData(gl.ARRAY_BUFFER, new Float32Array([
		0.0, -1.0, 0.0,
		0.0, 0.0, 1.0,
		-0.382683, 0.0, 0.92388,
		-0.707107, 0.0, 0.707107,
		-0.92388, 0.0, 0.382683,
		-1.0, 0.0, 0.0,
		-0.92388, 0.0, -0.382683,
		-0.707107, 0.0, -0.707107,
		-0.382683, 0.0, -0.92388,
		0.0, 0.0, -1.0,
		0.382683, 0.0, -0.92388,
		0.707107, 0.0, -0.707107,
		0.92388, 0.0, -0.382683,
		1.0, 0.0, 0.0,
		0.92388, 0.0, 0.382683,
		0.707107, 0.0, 0.707107,
		0.382683, 0.0, 0.92388,
		0.0, 0.0, 1.0
	]), gl.STATIC_DRAW);

	makeSky();
};

export const newMap = function () {
	const gl = GL.getContext()
	var i;
	for (i = 0; i < 64; ++i)
		lm.state.lightstylevalue[i] = 264;

	clearParticles();
	pscript.clearPScriptParticles();
	pscript.loadWorldWeather();  // async; guards internally against map changes racing its loads
	state.oldviewleaf = null
	state.cached_vis = null
	lm.init()
	mapAlpha.parseWorldspawn()
	fog.parseWorldspawn()
	sky.parseWorldspawn()

	// Same worldmodel respawned into the same GL context (savegame load,
	// restart, same-map changelevel): its VBO + lightmap textures are still
	// valid, so skip the geometry rebuild — on a huge map that rebuild is a
	// transient ~344MB Float32Array (VBO) plus lightmap staging, enough to blow
	// the heap when respawning an already-near-ceiling map. The world reuse in
	// mod.clearAll is what keeps the model object identity across the respawn.
	if (state.builtWorldmodel !== cl.clState.worldmodel || state.model_vbo == null) {
		for (i = 1; i < cl.clState.model_precache.length; ++i) {
			var model = cl.clState.model_precache[i];
			if (model.type !== mod.TYPE.brush)
				continue;
			if (model.name.charCodeAt(0) !== 42) {
				lm.buildLightmaps(gl, model);
				buildSurfaceDisplayLists(model)
			}
		}

		lm.freeStagingSlots()

		buildModelVertexBuffer(gl)
		state.builtWorldmodel = cl.clState.worldmodel

		// Force the GPU to process all queued uploads (lightmaps, VBOs) now,
		// during loading, rather than deferring to the first 3D draw call
		// which would cause a ~400ms stall at the start of gameplay.
		gl.flush()
	}
};

export const timeRefresh_f = function () {
	const gl = GL.getContext()
	gl.finish();
	var i;
	var start = sys.floatTime();
	for (i = 0; i <= 127; ++i) {
		state.refdef.viewangles[1] = i * 2.8125;
		renderView();
	}
	gl.finish();
	var time = sys.floatTime() - start;
	con.print(time.toFixed(6) + ' seconds (' + (128.0 / time).toFixed(6) + ' fps)\n');
};

// part

const PTYPE = {
	tracer: 0,
	grav: 1,
	slowgrav: 2,
	fire: 3,
	explode: 4,
	explode2: 5,
	blob: 6,
	blob2: 7
};

state.ramp1 = [0x6f, 0x6d, 0x6b, 0x69, 0x67, 0x65, 0x63, 0x61];
state.ramp2 = [0x6f, 0x6e, 0x6d, 0x6c, 0x6b, 0x6a, 0x68, 0x66];
state.ramp3 = [0x6d, 0x6b, 6, 5, 4, 3];

export const initParticles = function () {
	const gl = GL.getContext()
	var i = com.checkParm('-particles');
	if (i != null) {
		state.numparticles = q.atoi(com.state.argv[i + 1]);
		if (state.numparticles < 512)
			state.numparticles = 512;
	}
	else
		state.numparticles = 2048;

	state.avelocities = [];
	for (i = 0; i <= 161; ++i)
		state.avelocities[i] = [Math.random() * 2.56, Math.random() * 2.56, Math.random() * 2.56];

	state.particleOrg = new Float32Array(state.numparticles * 3);
	state.particleVel = new Float32Array(state.numparticles * 3);
	state.particleRamp = new Float32Array(state.numparticles);
	state.particleDie = new Float32Array(state.numparticles);
	state.particleColor = new Uint8Array(state.numparticles);
	state.particleType = new Uint8Array(state.numparticles);
	state.numActiveParticles = 0;

	state.particleInstanceData = new ArrayBuffer(state.numparticles * 16);
	state.particleInstanceFloats = new Float32Array(state.particleInstanceData);
	state.particleInstanceBytes = new Uint8Array(state.particleInstanceData);

	GL.createProgram('Particle',
		['uViewOrigin', 'uViewAngles', 'uPerspective', 'uGamma', 'uVpn'],
		[
			createAttribParam('aCorner', gl.FLOAT, 2),
			createAttribParam('aOrigin', gl.FLOAT, 3),
			createAttribParam('aColor', gl.UNSIGNED_BYTE, 4, true)
		],
		[]);

	state.particleCornerBuffer = gl.createBuffer();
	gl.bindBuffer(gl.ARRAY_BUFFER, state.particleCornerBuffer);
	gl.bufferData(gl.ARRAY_BUFFER, new Float32Array([-1.0, -1.0, -1.0, 1.0, 1.0, -1.0, 1.0, 1.0]), gl.STATIC_DRAW);

	state.particleInstanceBuffer = gl.createBuffer();
	gl.bindBuffer(gl.ARRAY_BUFFER, state.particleInstanceBuffer);
	gl.bufferData(gl.ARRAY_BUFFER, state.particleInstanceData.byteLength, gl.DYNAMIC_DRAW);
};

// Internal slot allocator: reserves the next free particle index, or -1 if
// the pool is full.
const spawnParticle = function (): number {
	if (state.numActiveParticles >= state.numparticles)
		return -1;
	return state.numActiveParticles++;
};

// Allocates a particle and writes all its SoA fields in one place. Returns
// false when the pool is full (emission dropped).
const emitParticle = (ox: number, oy: number, oz: number, vx: number, vy: number, vz: number, die: number, color: number, ramp: number, type: number): boolean => {
	var idx = spawnParticle();
	if (idx < 0)
		return false;
	var i3 = idx * 3;
	state.particleOrg[i3] = ox;
	state.particleOrg[i3 + 1] = oy;
	state.particleOrg[i3 + 2] = oz;
	state.particleVel[i3] = vx;
	state.particleVel[i3 + 1] = vy;
	state.particleVel[i3 + 2] = vz;
	state.particleDie[idx] = die;
	state.particleColor[idx] = color;
	state.particleRamp[idx] = ramp;
	state.particleType[idx] = type;
	return true;
};

export const entityParticles = function (ent: Entity) {
	var angle, sp, sy, cp, cy;
	for (var i = 0; i <= 161; ++i) {
		angle = cl.clState.time * state.avelocities[i][0];
		sp = Math.sin(angle);
		cp = Math.cos(angle);
		angle = cl.clState.time * state.avelocities[i][1];
		sy = Math.sin(angle);
		cy = Math.cos(angle);

		var ox = ent.origin[0] + state.avertexnormals[i][0] * 64.0 + cp * cy * 16.0;
		var oy = ent.origin[1] + state.avertexnormals[i][1] * 64.0 + cp * sy * 16.0;
		var oz = ent.origin[2] + state.avertexnormals[i][2] * 64.0 + sp * -16.0;
		if (!emitParticle(ox, oy, oz, 0.0, 0.0, 0.0, cl.clState.time + 0.01, 0x6f, 0.0, PTYPE.explode))
			return;
	}
};

export const clearParticles = function () {
	state.numActiveParticles = 0;
};

export const readPointFile_f = async function () {
	if (sv.state.server.active !== true)
		return;
	var name = 'maps/' + pr.getString(pr.state.globals_int[pr.globalvars.mapname]) + '.pts';
	var f = await com.loadTextFile(name);
	if (f == null) {
		con.print('couldn\'t open ' + name + '\n');
		return;
	}
	con.print('Reading ' + name + '...\n');
	var flines = f.split('\n');
	var c, org;
	for (c = 0; c < flines.length;) {
		org = flines[c].split(' ');
		if (org.length !== 3)
			break;
		++c;
		var ox = q.atof(org[0]), oy = q.atof(org[1]), oz = q.atof(org[2]);
		if (!emitParticle(ox, oy, oz, 0.0, 0.0, 0.0, 99999.0, -c & 15, 0, PTYPE.tracer)) {
			con.print('Not enough free particles\n');
			break;
		}
	}
	con.print(c + ' points read\n');
};

export const parseParticleEffect = function () {
	var org: V3 = [msg.readCoord(cl.clState.protocolFlags), msg.readCoord(cl.clState.protocolFlags), msg.readCoord(cl.clState.protocolFlags)];
	var dir: V3 = [msg.readChar() * 0.0625, msg.readChar() * 0.0625, msg.readChar() * 0.0625];
	var msgcount = msg.readByte();
	var color = msg.readByte();
	if (msgcount === 255)
		particleExplosion(org);
	else
		runParticleEffect(org, dir, color, msgcount);
};

export const particleExplosion = function (org: V3) {
	for (var i = 0; i < 1024; ++i) {
		var ramp = Math.floor(Math.random() * 4.0);
		var type = ((i & 1) !== 0) ? PTYPE.explode : PTYPE.explode2;
		var ox = org[0] + Math.random() * 32.0 - 16.0;
		var oy = org[1] + Math.random() * 32.0 - 16.0;
		var oz = org[2] + Math.random() * 32.0 - 16.0;
		var vx = Math.random() * 512.0 - 256.0;
		var vy = Math.random() * 512.0 - 256.0;
		var vz = Math.random() * 512.0 - 256.0;
		if (!emitParticle(ox, oy, oz, vx, vy, vz, cl.clState.time + 5.0, state.ramp1[0], ramp, type))
			return;
	}
};

export const particleExplosion2 = function (org: V3, colorStart: number, colorLength: number) {
	var colorMod = 0;
	for (var i = 0; i < 512; ++i) {
		var color = colorStart + (colorMod++ % colorLength);
		var ox = org[0] + Math.random() * 32.0 - 16.0;
		var oy = org[1] + Math.random() * 32.0 - 16.0;
		var oz = org[2] + Math.random() * 32.0 - 16.0;
		var vx = Math.random() * 512.0 - 256.0;
		var vy = Math.random() * 512.0 - 256.0;
		var vz = Math.random() * 512.0 - 256.0;
		if (!emitParticle(ox, oy, oz, vx, vy, vz, cl.clState.time + 0.3, color, 0, PTYPE.blob))
			return;
	}
};

export const blobExplosion = function (org: V3) {
	for (var i = 0; i < 1024; ++i) {
		var type, color;
		if ((i & 1) !== 0) {
			type = PTYPE.blob;
			color = 66 + Math.floor(Math.random() * 7.0);
		}
		else {
			type = PTYPE.blob2;
			color = 150 + Math.floor(Math.random() * 7.0);
		}
		var ox = org[0] + Math.random() * 32.0 - 16.0;
		var oy = org[1] + Math.random() * 32.0 - 16.0;
		var oz = org[2] + Math.random() * 32.0 - 16.0;
		var vx = Math.random() * 512.0 - 256.0;
		var vy = Math.random() * 512.0 - 256.0;
		var vz = Math.random() * 512.0 - 256.0;
		// ramp isn't used by blob/blob2 in runParticles, so 0 is safe here.
		if (!emitParticle(ox, oy, oz, vx, vy, vz, cl.clState.time + 1.0 + Math.random() * 0.4, color, 0, type))
			return;
	}
};

export const runParticleEffect = function (org: V3, dir: V3, color: number, count: number) {
	for (var i = 0; i < count; ++i) {
		var ox = org[0] + Math.random() * 16.0 - 8.0;
		var oy = org[1] + Math.random() * 16.0 - 8.0;
		var oz = org[2] + Math.random() * 16.0 - 8.0;
		var c = (color & 0xf8) + Math.floor(Math.random() * 8.0);
		var die = cl.clState.time + 0.6 * Math.random();
		if (!emitParticle(ox, oy, oz, dir[0] * 15.0, dir[1] * 15.0, dir[2] * 15.0, die, c, 0, PTYPE.slowgrav))
			return;
	}
};

export const lavaSplash = function (org: V3) {
	var dir = vec.emptyV3(), vel;
	for (var i = -16; i <= 15; ++i) {
		for (var j = -16; j <= 15; ++j) {
			dir[0] = (j + Math.random()) * 8.0;
			dir[1] = (i + Math.random()) * 8.0;
			dir[2] = 256.0;
			var ox = org[0] + dir[0];
			var oy = org[1] + dir[1];
			var oz = org[2] + Math.random() * 64.0;
			vec.normalize(dir);
			vel = 50.0 + Math.random() * 64.0;
			var color = 224 + Math.floor(Math.random() * 8.0);
			var die = cl.clState.time + 2.0 + Math.random() * 0.64;
			if (!emitParticle(ox, oy, oz, dir[0] * vel, dir[1] * vel, dir[2] * vel, die, color, 0, PTYPE.slowgrav))
				return;
		}
	}
};

export const teleportSplash = function (org: V3) {
	var dir = vec.emptyV3(), vel;
	for (var i = -16; i <= 15; i += 4) {
		for (var j = -16; j <= 15; j += 4) {
			for (var k = -24; k <= 31; k += 4) {
				dir[0] = j * 8.0;
				dir[1] = i * 8.0;
				dir[2] = k * 8.0;
				var ox = org[0] + i + Math.random() * 4.0;
				var oy = org[1] + j + Math.random() * 4.0;
				var oz = org[2] + k + Math.random() * 4.0;
				vec.normalize(dir);
				vel = 50.0 + Math.random() * 64.0;
				var color = 7 + Math.floor(Math.random() * 8.0);
				var die = cl.clState.time + 0.2 + Math.random() * 0.16;
				if (!emitParticle(ox, oy, oz, dir[0] * vel, dir[1] * vel, dir[2] * vel, die, color, 0, PTYPE.slowgrav))
					return;
			}
		}
	}
};

export const rocketTrail = function (start: V3, end: V3, type: number) {
	var dx = end[0] - start[0], dy = end[1] - start[1], dz = end[2] - start[2];
	var len = Math.sqrt(dx * dx + dy * dy + dz * dz);
	if (len === 0.0)
		return;
	dx /= len; dy /= len; dz /= len;

	var count = (type === 4) ? Math.floor(len / 6.0) : Math.floor(len / 3.0);

	for (var i = 0; i < count; ++i) {
		// Peek pool capacity before the switch below: case 3/5 mutates
		// state.tracercount, a persistent module state field, and that must
		// not fire on an iteration whose particle gets dropped.
		if (state.numActiveParticles >= state.numparticles)
			return;

		var die = cl.clState.time + 2.0;
		var vx = 0.0, vy = 0.0, vz = 0.0;
		var ox = start[0], oy = start[1], oz = start[2];
		var ptype = PTYPE.fire, color = 0, ramp = 0;
		switch (type) {
			case 0:
			case 1:
				ramp = Math.floor(Math.random() * 4.0) + (type << 1);
				color = state.ramp3[ramp];
				ptype = PTYPE.fire;
				ox = start[0] + Math.random() * 6.0 - 3.0;
				oy = start[1] + Math.random() * 6.0 - 3.0;
				oz = start[2] + Math.random() * 6.0 - 3.0;
				break;
			case 2:
			case 4:
				ptype = PTYPE.grav;
				color = 67 + Math.floor(Math.random() * 4.0);
				ox = start[0] + Math.random() * 6.0 - 3.0;
				oy = start[1] + Math.random() * 6.0 - 3.0;
				oz = start[2] + Math.random() * 6.0 - 3.0;
				break;
			case 3:
			case 5:
				die = cl.clState.time + 0.5;
				ptype = PTYPE.tracer;
				if (type === 3)
					color = 52 + ((state.tracercount++ & 4) << 1);
				else
					color = 230 + ((state.tracercount++ & 4) << 1);
				if ((state.tracercount & 1) !== 0) {
					vx = 30.0 * dy;
					vz = -30.0 * dx;
				}
				else {
					vx = -30.0 * dy;
					vz = 30.0 * dx;
				}
				break;
			case 6:
				color = 152 + Math.floor(Math.random() * 4.0);
				ptype = PTYPE.tracer;
				die = cl.clState.time + 0.3;
				ox = start[0] + Math.random() * 16.0 - 8.0;
				oy = start[1] + Math.random() * 16.0 - 8.0;
				oz = start[2] + Math.random() * 16.0 - 8.0;
		}
		emitParticle(ox, oy, oz, vx, vy, vz, die, color, ramp, ptype);

		start[0] += dx;
		start[1] += dy;
		start[2] += dz;
	}
};

export const runParticles = function () {
	var frametime = cl.clState.time - cl.clState.oldtime;
	var grav = frametime * sv.cvr.gravity.value * 0.05;
	var dvel = frametime * 4.0;

	var i = 0;
	while (i < state.numActiveParticles) {
		if (state.particleDie[i] < cl.clState.time) {
			var last = --state.numActiveParticles;
			if (i !== last) {
				var i3 = i * 3, l3 = last * 3;
				state.particleOrg[i3] = state.particleOrg[l3];
				state.particleOrg[i3 + 1] = state.particleOrg[l3 + 1];
				state.particleOrg[i3 + 2] = state.particleOrg[l3 + 2];
				state.particleVel[i3] = state.particleVel[l3];
				state.particleVel[i3 + 1] = state.particleVel[l3 + 1];
				state.particleVel[i3 + 2] = state.particleVel[l3 + 2];
				state.particleRamp[i] = state.particleRamp[last];
				state.particleDie[i] = state.particleDie[last];
				state.particleColor[i] = state.particleColor[last];
				state.particleType[i] = state.particleType[last];
			}
			continue;
		}

		var i3 = i * 3;
		state.particleOrg[i3] += state.particleVel[i3] * frametime;
		state.particleOrg[i3 + 1] += state.particleVel[i3 + 1] * frametime;
		state.particleOrg[i3 + 2] += state.particleVel[i3 + 2] * frametime;

		switch (state.particleType[i]) {
			case PTYPE.fire:
				state.particleRamp[i] += frametime * 5.0;
				if (state.particleRamp[i] >= 6.0)
					state.particleDie[i] = -1.0;
				else
					state.particleColor[i] = state.ramp3[Math.floor(state.particleRamp[i])];
				state.particleVel[i3 + 2] += grav;
				break;
			case PTYPE.explode:
				state.particleRamp[i] += frametime * 10.0;
				if (state.particleRamp[i] >= 8.0)
					state.particleDie[i] = -1.0;
				else
					state.particleColor[i] = state.ramp1[Math.floor(state.particleRamp[i])];
				state.particleVel[i3] += state.particleVel[i3] * dvel;
				state.particleVel[i3 + 1] += state.particleVel[i3 + 1] * dvel;
				state.particleVel[i3 + 2] += state.particleVel[i3 + 2] * dvel - grav;
				break;
			case PTYPE.explode2:
				state.particleRamp[i] += frametime * 15.0;
				if (state.particleRamp[i] >= 8.0)
					state.particleDie[i] = -1.0;
				else
					state.particleColor[i] = state.ramp2[Math.floor(state.particleRamp[i])];
				state.particleVel[i3] -= state.particleVel[i3] * frametime;
				state.particleVel[i3 + 1] -= state.particleVel[i3 + 1] * frametime;
				state.particleVel[i3 + 2] -= state.particleVel[i3 + 2] * frametime + grav;
				break;
			case PTYPE.blob:
				state.particleVel[i3] += state.particleVel[i3] * dvel;
				state.particleVel[i3 + 1] += state.particleVel[i3 + 1] * dvel;
				state.particleVel[i3 + 2] += state.particleVel[i3 + 2] * dvel - grav;
				break;
			case PTYPE.blob2:
				state.particleVel[i3] += state.particleVel[i3] * dvel;
				state.particleVel[i3 + 1] += state.particleVel[i3 + 1] * dvel;
				state.particleVel[i3 + 2] -= grav;
				break;
			case PTYPE.grav:
			case PTYPE.slowgrav:
				state.particleVel[i3 + 2] -= grav;
				break;
		}
		++i;
	}
};

// Quad corners as two CCW triangles, matching the instanced TRIANGLE_STRIP
// coverage.
const particleCoords = [-1.0, -1.0, -1.0, 1.0, 1.0, -1.0, 1.0, -1.0, -1.0, 1.0, 1.0, 1.0];

// Streams two triangles per particle through the shared stream buffer using
// the same Particle program (aCorner/aOrigin/aColor per vertex). Only used
// when instancing is unavailable (WebGL1 without ANGLE_instanced_arrays).
const drawParticlesStream = function () {
	const gl = GL.getContext()
	GL.streamFlush();
	GL.useProgram('Particle');
	gl.depthMask(false);
	gl.enable(gl.BLEND);
	var coords = particleCoords;
	for (var i = 0; i < state.numActiveParticles; ++i) {
		var i3 = i * 3;
		var ox = state.particleOrg[i3], oy = state.particleOrg[i3 + 1], oz = state.particleOrg[i3 + 2];
		var color = vid.d_8to24table[state.particleColor[i]];
		var cr = color & 0xff, cg = (color >> 8) & 0xff, cb = color >> 16;
		GL.streamGetSpace(6);
		for (var j = 0; j < 6; ++j) {
			GL.streamWriteFloat2(coords[j * 2], coords[j * 2 + 1]);
			GL.streamWriteFloat3(ox, oy, oz);
			GL.streamWriteUByte4(cr, cg, cb, 255);
		}
	}
	GL.streamFlush();
	gl.disable(gl.BLEND);
	gl.depthMask(true);
};

export const drawParticles = function () {
	if (state.numActiveParticles === 0)
		return;

	if (GL.state.instancingSupported !== true) {
		drawParticlesStream();
		return;
	}

	const gl = GL.getContext()
	GL.streamFlush();

	var program = GL.useProgram('Particle');
	gl.depthMask(false);
	gl.enable(gl.BLEND);

	var floats = state.particleInstanceFloats;
	var bytes = state.particleInstanceBytes;
	for (var i = 0; i < state.numActiveParticles; ++i) {
		var fBase = i * 4, bBase = i * 16, i3 = i * 3;
		floats[fBase] = state.particleOrg[i3];
		floats[fBase + 1] = state.particleOrg[i3 + 1];
		floats[fBase + 2] = state.particleOrg[i3 + 2];
		var color = vid.d_8to24table[state.particleColor[i]];
		bytes[bBase + 12] = color & 0xff;
		bytes[bBase + 13] = (color >> 8) & 0xff;
		bytes[bBase + 14] = color >> 16;
		bytes[bBase + 15] = 255;
	}

	gl.bindBuffer(gl.ARRAY_BUFFER, state.particleInstanceBuffer);
	gl.bufferData(gl.ARRAY_BUFFER, state.particleInstanceData.byteLength, gl.DYNAMIC_DRAW);
	// WebGL2's 5-arg overload uploads a prefix without allocating a subarray
	// view; the view fallback only runs on WebGL1-with-ANGLE contexts.
	if (GL.state.isWebGL2)
		gl.bufferSubData(gl.ARRAY_BUFFER, 0, bytes, 0, state.numActiveParticles * 16);
	else
		gl.bufferSubData(gl.ARRAY_BUFFER, 0, bytes.subarray(0, state.numActiveParticles * 16));

	var aCorner = program.attributeMap.aCorner;
	var aOrigin = program.attributeMap.aOrigin;
	var aColor = program.attributeMap.aColor;

	gl.bindBuffer(gl.ARRAY_BUFFER, state.particleCornerBuffer);
	gl.vertexAttribPointer(aCorner.location, 2, gl.FLOAT, false, 0, 0);
	GL.state.vertexAttribDivisor(aCorner.location, 0);

	gl.bindBuffer(gl.ARRAY_BUFFER, state.particleInstanceBuffer);
	gl.vertexAttribPointer(aOrigin.location, 3, gl.FLOAT, false, 16, 0);
	gl.vertexAttribPointer(aColor.location, 4, gl.UNSIGNED_BYTE, true, 16, 12);
	GL.state.vertexAttribDivisor(aOrigin.location, 1);
	GL.state.vertexAttribDivisor(aColor.location, 1);

	GL.state.drawArraysInstanced(gl.TRIANGLE_STRIP, 0, 4, state.numActiveParticles);

	GL.state.vertexAttribDivisor(aOrigin.location, 0);
	GL.state.vertexAttribDivisor(aColor.location, 0);

	gl.disable(gl.BLEND);
	gl.depthMask(true);
};

// surf

state.lightmap_modified = [];

export const textureAnimation = function (model: Model, base: Texture, entFrame: number) {
	var frame = 0;
	if (base.anim_base != null) {
		frame = base.anim_frame;
		base = model.textures[base.anim_base];
	}
	var anims = base.anims;
	if (anims == null)
		return base;
	if ((entFrame !== 0) && (base.alternate_anims.length !== 0))
		anims = base.alternate_anims;
	return model.textures[anims[(Math.floor(cl.clState.time * 5.0) + frame) % anims.length]];
};

const clearTextureChains = (model: Model, chain: TexChain) => {
	// set all chains to null — restricted to textures this model's own faces
	// can reference (chainSurface only ever chains a model's own faces)
	var used = model.usedTextures
	for (var i = 0; i < used.length; i++) {
		var t = model.textures[used[i]]
		if (t && t.texturechains)
			t.texturechains[chain] = null;
	}
}

export const drawBrushModel = function (e: Entity) {
	const gl = GL.getContext()
	var clmodel = e.model;

	if (clmodel.submodel === true) {
		var cullMins = state.cullMins, cullMaxs = state.cullMaxs;
		cullMins[0] = e.origin[0] + clmodel.mins[0];
		cullMins[1] = e.origin[1] + clmodel.mins[1];
		cullMins[2] = e.origin[2] + clmodel.mins[2];
		cullMaxs[0] = e.origin[0] + clmodel.maxs[0];
		cullMaxs[1] = e.origin[1] + clmodel.maxs[1];
		cullMaxs[2] = e.origin[2] + clmodel.maxs[2];
		if (cullBox(cullMins, cullMaxs) === true)
			return;
	}
	else {
		var cullMins = state.cullMins, cullMaxs = state.cullMaxs;
		cullMins[0] = e.origin[0] - clmodel.radius;
		cullMins[1] = e.origin[1] - clmodel.radius;
		cullMins[2] = e.origin[2] - clmodel.radius;
		cullMaxs[0] = e.origin[0] + clmodel.radius;
		cullMaxs[1] = e.origin[1] + clmodel.radius;
		cullMaxs[2] = e.origin[2] + clmodel.radius;
		if (cullBox(cullMins, cullMaxs) === true)
			return;
	}

	clearTextureChains(clmodel, TexChain.model);
	var modelOrg = vec.subtract(state.refdef.vieworg, e.origin, vec.scratch())
	if (e.angles[0] || e.angles[1] || e.angles[2]) {
		var temp = vec.scratch()
		var forward = vec.scratch(), right = vec.scratch(), up = vec.scratch()
		vec.copy(modelOrg, temp)

		vec.angleVectors(e.angles, forward, right, up);
		modelOrg[0] = vec.dotProductV3(temp, forward);
		modelOrg[1] = -vec.dotProductV3(temp, right);
		modelOrg[2] = vec.dotProductV3(temp, up);
	}
	// firstmodelsurface is not used TODO: Joe Should this be the "first face" on a submodel
	// if (clmodel.firstmodelsurface != 0 && !cvr.flashblend.value) {
	// 	for (var k = 0; k < cl.state.dlights.length; k++) {
	// 		if ((cl.state.dlights[k].die < cl.clState.time) ||
	// 			(!cl.state.dlights[k].radius))
	// 			continue;

	// 		markLights(cl.state.dlights[k], k, cl.clState.worldmodel.nodes[clmodel.hulls[0].firstclipnode]);
	// 	}
	// }

	for (var i = 0; i < clmodel.numfaces; i++) {
		var surf = clmodel.faces[clmodel.firstface + i]
		var pplane = surf.plane;
		var dot = vec.dotProductV3(modelOrg, pplane.normal) - pplane.dist;
		if (((surf.flags & def.SURF.planeback) && (dot < -0.01)) ||
			(!(surf.flags & def.SURF.planeback) && (dot > 0.01))) {
			chainSurface(clmodel, surf, TexChain.model);
		}
	}

	drawTextureChains(gl, e.model, e, TexChain.model)
	drawTextureChains_litwater(gl, e.model, e, TexChain.model)
	drawTextureChains_water(gl, e.model, e, TexChain.model)
};

// Hierarchical frustum walk over the flat world BSP. Every face belongs to
// exactly one interior node (nodePacked firstFace/numFaces) whose bbox
// contains it, and that owning node is always an ancestor of any visible leaf that
// chains the face, so nodeMarkvisframe alone (no leaf test) is a sound PVS
// gate for stamping it. A subtree already known out of the PVS or fully
// outside a frustum plane is skipped without testing any faces; a subtree
// found fully inside a plane drops that plane's bit from mask so descendants
// don't retest it. Every face owned by a node lies on that node's split
// plane (marked front/back via surfPlaneBack), so the view-side of the
// plane is identical for all of them and is computed once per node — only
// the front-facing half of the node's face range gets stamped with
// state.frustumFrame. Leaves carry no faces and are never visited.
export const markWorldFrustum = () => {
	// |0 keeps the counter inside int32 so it always equals the truncated
	// values the branchless stamp stores in surfVisibleFrame (Int32Array).
	state.frustumFrame = (state.frustumFrame + 1) | 0;

	// Iterative with every array and scalar hoisted to locals: the walk visits
	// thousands of nodes per frame, and per-visit property chains (state.*,
	// model.*, refdef.vieworg[i]) or recursion overhead dominate its cost.
	// Per-node fields (bbox/plane/faces/children) come from nodePacked, one
	// interleaved 64B record per node (see Model.nodePacked), so a visited
	// node costs 1-2 cache lines instead of touching 7 separate arrays.
	var model = cl.clState.worldmodel;
	var markvis = model.nodeMarkvisframe, visframe = state.visframecount;
	var packed = model.nodePacked, packedI32 = model.nodePackedI32;
	var planeBack = model.surfPlaneBack, visible = model.surfVisibleFrame;
	var frustum = state.frustumFlat, signbits = state.frustumSignbits;
	var stampFrame = state.frustumFrame;
	var vieworg = state.refdef.vieworg;
	var vx = vieworg[0], vy = vieworg[1], vz = vieworg[2];
	// DFS holds at most depth+1 pending (idx, mask) pairs; typed arrays drop
	// out-of-bounds writes silently, so an undersized stack would silently cull
	// subtrees. Regrow from the load-time depth bound (at most once per map).
	var need = (model.bspMaxDepth + 2) * 2;
	if (state.frustumWalkStack.length < need)
		state.frustumWalkStack = new Int32Array(need);
	var stack = state.frustumWalkStack;

	stack[0] = 0;
	stack[1] = 0b1111;
	var sp = 2;
	while (sp > 0) {
		sp -= 2;
		var idx = stack[sp], mask = stack[sp + 1];
		if (markvis[idx] !== visframe)
			continue;
		var base = idx * 16;
		if (mask !== 0) {
			var culled = false;
			for (var i = 0; i < 4; i++) {
				var bit = 1 << i;
				if ((mask & bit) === 0)
					continue;
				var o = i * 4;
				var nx = frustum[o], ny = frustum[o + 1], nz = frustum[o + 2], dist = frustum[o + 3];
				var sb = signbits[i];
				var px = (sb & 1) !== 0 ? packed[base] : packed[base + 3];
				var py = (sb & 2) !== 0 ? packed[base + 1] : packed[base + 4];
				var pz = (sb & 4) !== 0 ? packed[base + 2] : packed[base + 5];
				if (nx * px + ny * py + nz * pz < dist) {
					culled = true;
					break;
				}
				var nvx = (sb & 1) !== 0 ? packed[base + 3] : packed[base];
				var nvy = (sb & 2) !== 0 ? packed[base + 4] : packed[base + 1];
				var nvz = (sb & 4) !== 0 ? packed[base + 5] : packed[base + 2];
				if (nx * nvx + ny * nvy + nz * nvz >= dist)
					mask &= ~bit;
			}
			if (culled)
				continue;
		}

		var pt = packedI32[base + 10];
		var pdist = packed[base + 9];
		var dot = pt < 3
			? vieworg[pt] - pdist
			: vx * packed[base + 6] + vy * packed[base + 7] + vz * packed[base + 8] - pdist;
		var side = dot < 0 ? 1 : 0;
		var first = packedI32[base + 11], end = first + packedI32[base + 12];
		// Branchless: a face is kept when planeBack === side. Each face belongs
		// to exactly one node, so a rejected face can't have been stamped this
		// frame by anyone else — writing 0 instead of skipping keeps the write
		// stream unconditional (no per-face branch misprediction).
		for (var f = first; f < end; f++)
			visible[f] = stampFrame & ((planeBack[f] ^ side) - 1);

		var c0 = packedI32[base + 13], c1 = packedI32[base + 14];
		if (c0 >= 0) {
			stack[sp] = c0;
			stack[sp + 1] = mask;
			sp += 2;
		}
		if (c1 >= 0) {
			stack[sp] = c1;
			stack[sp + 1] = mask;
			sp += 2;
		}
	}
};

// Flags-int variant so the world draw loop never dereferences a Face object.
const waterAlphaForFlags = (flags: number) => {
	if (flags & def.SURF.drawlava)
		return mapAlpha.state.lava > 0 ? mapAlpha.state.lava : mapAlpha.state.water;
	else if (flags & def.SURF.drawtele)
		return mapAlpha.state.tele > 0 ? mapAlpha.state.tele : mapAlpha.state.water;
	else if (flags & def.SURF.drawslime)
		return mapAlpha.state.slime > 0 ? mapAlpha.state.slime : mapAlpha.state.water;
	else
		return mapAlpha.state.water;
}

const waterAlphaForSurface = (surf: Face) => waterAlphaForFlags(surf.flags)

// Blend state toggle when a water surface's alpha differs from the bound one.
const applyWaterAlpha = (gl: WebGL2RenderingContext, program: GL.GLProgram, newalpha: number) => {
	if (newalpha < 1)
	{
		gl.depthMask(false);
		gl.enable(gl.BLEND);
		gl.blendFunc(gl.SRC_ALPHA, gl.ONE_MINUS_SRC_ALPHA);
	} else {
		gl.depthMask(true);
		gl.disable(gl.BLEND);
	}
	gl.uniform1f(program.uniforms.uAlpha, newalpha);
}


/*
================
GL_WaterAlphaForEntitySurface -- ericw
 
Returns the water alpha to use for the entity and surface combination.
================
*/
const waterAlphaForEntitySurface = (ent: Entity, surf: Face) => {
	var entalpha = 1
	if (!ent || ent.alpha == 1)
		entalpha = waterAlphaForSurface(surf);
	else
		entalpha = pr.decodeAlpha(ent.alpha);
	return entalpha;
}

// r_litwater: a turb surface with real lightmap samples (mod.ts only sets
// drawtub without drawtiled when the texinfo isn't TEX.special and lightofs
// is valid — Ironwail gl_model.c:1384-1391). Such surfaces draw through
// drawTextureChains_litwater instead of the classic unlit Turbulent path.
const isLitWaterFlags = (flags: number) => (flags & def.SURF.drawtub) !== 0 && !(flags & def.SURF.drawtiled)

// Bind all 4 lightmap style units (1,3,4,5) for a page. Shared by the solid
// and lit-water Brush passes.
// TMU 2 = FullbrightTex: additive luma companion for split fullbright textures (see mod.loadTextures)
const bindFullbrightTexture = (gl: WebGL2RenderingContext, program: ReturnType<typeof GL.useProgram>, texture: Texture) => {
	if (cvr.fullbrights.value && texture.fullbright) {
		tx.bind(2, texture.fullbright)
		gl.uniform1i(program.uniforms.uUseFullbrightTex, 1)
	} else {
		tx.bind(2, tx.state.null_texture)
		gl.uniform1i(program.uniforms.uUseFullbrightTex, 0)
	}
}

const bindLightmapPageTextures = (gl: WebGL2RenderingContext, page: number) => {
	const pageSlots = tx.state.lightmap_style_textures[page]
	const black = tx.state.black_texture
	tx.bind(1, pageSlots && pageSlots[0] ? pageSlots[0].texnum : black)
	tx.bind(3, pageSlots && pageSlots[1] ? pageSlots[1].texnum : black)
	tx.bind(4, pageSlots && pageSlots[2] ? pageSlots[2].texnum : black)
	tx.bind(5, pageSlots && pageSlots[3] ? pageSlots[3].texnum : black)
}

/*
================
R_DrawTextureChains_Water -- johnfitz
================
*/
const drawTextureChains_water = (gl: WebGL2RenderingContext, model: Model, ent: Entity, chain: TexChain) => {

	// No water chained for this model? Skip before touching the program —
	// most brush entities have no water, and switching Turbulent<->Brush per
	// entity forces the driver to revalidate the heavyweight Brush program.
	var anyWater = false
	var usedTex = model.usedTextures
	for (var ui = 0; ui < usedTex.length; ui++) {
		var t = model.textures[usedTex[ui]];
		if (t && t.texturechains && t.texturechains[chain]
			&& (t.texturechains[chain].flags & def.SURF.drawtub)) {
			anyWater = true
			break
		}
	}
	if (!anyWater)
		return

	// Lit turb surfaces draw via drawTextureChains_litwater instead — skip
	// them here so they aren't drawn twice.
	var litwaterActive = cvr.litwater.value !== 0 && cl.clState.worldmodel.haslitwater

	// World chains hold every PVS-visible surface regardless of facing;
	// backface + frustum culling for them is the surfVisibleFrame stamp from
	// markWorldFrustum. Entity chains (drawBrushModel) are built by walking
	// only front-facing surfaces in the first place, so nothing further to
	// check here.
	var isWorld = model === cl.clState.worldmodel && chain === TexChain.world
	var visibleStamp = model.surfVisibleFrame, stampFrame = state.frustumFrame

	const turbulentProgram = GL.useProgram('Turbulent')

	// Bind the buffers
	gl.bindBuffer(gl.ARRAY_BUFFER, state.model_vbo);
	gl.bindBuffer(gl.ELEMENT_ARRAY_BUFFER, null)// indices come from client memory!

	gl.vertexAttribPointer(turbulentProgram.attributeMap.aPosition.location, 3, gl.FLOAT, false, def.VERTEXSIZE * 4, 0);
	gl.vertexAttribPointer(turbulentProgram.attributeMap.aTexCoord.location, 2, gl.FLOAT, false, def.VERTEXSIZE * 4, 4 * 3);

	// set uniforms
	gl.uniform1i(turbulentProgram.uniforms.uUseOverbright, cvr.overbright.value);
	gl.uniform1i(turbulentProgram.uniforms.uUseAlphaTest, 0);

	gl.uniform3f(turbulentProgram.uniforms.uOrigin, 0.0, 0.0, 0.0);
	gl.uniformMatrix3fv(turbulentProgram.uniforms.uAngles, false, GL.identity);
	gl.uniform1f(turbulentProgram.uniforms.uTime, host.state.realtime % (Math.PI * 2.0))

	// World scans every texture slot (worldChainOfs/Count are indexed by
	// absolute texture index); entities scan only their own usedTextures.
	var texCount = isWorld ? model.textures.length : usedTex.length
	for (var ti = 0; ti < texCount; ti++) {
		var i = isWorld ? ti : usedTex[ti]
		var t = model.textures[i];
		if (!t || !t.texturechains || !t.texturechains[chain] || !(t.texturechains[chain].flags & def.SURF.drawtub))
			continue;
		var animatedTexture = textureAnimation(state.cl_worldmodel, t, ent != null ? ent.frame : 0)
		batchRender.clearBatch();
		var bound = false;
		var entalpha = 0

		if (isWorld) {
			// ent is always null for the world chain, so newalpha is always
			// waterAlphaForFlags(flags) — no entity-alpha branch needed.
			var ofs = model.worldChainOfs[i], count = model.worldChainCount[i]
			var chainFaces = model.worldChainFaces, flagsArr = model.surfFlags
			for (var ci = 0; ci < count; ci++) {
				var f = chainFaces[ofs + ci]
				if (visibleStamp[f] !== stampFrame)
					continue
				if (litwaterActive && isLitWaterFlags(flagsArr[f]))
					continue
				if (!bound) {
					tx.bind(0, animatedTexture.texturenum);
					bound = true;
				}
				var newalpha = waterAlphaForFlags(flagsArr[f]);
				if (newalpha !== entalpha)
					applyWaterAlpha(gl, turbulentProgram, newalpha);
				entalpha = newalpha
				batchRender.batchSurfaceRange(gl, model, f);
			}
		} else {
			for (var s = t.texturechains[chain]; s; s = s.texturechain) {
				if (litwaterActive && isLitWaterFlags(s.flags))
					continue
				if (!bound) //only bind once we are sure we need this texture
				{
					tx.bind(0, animatedTexture.texturenum);
					bound = true;
				}

				var	newalpha = waterAlphaForEntitySurface (ent, s);
				if (newalpha !== entalpha)
					applyWaterAlpha(gl, turbulentProgram, newalpha);
				entalpha = newalpha

				batchRender.batchSurface(gl, model, s);
			}
		}

		//R_EndTransparentDrawing (entalpha);
		batchRender.flushBatch(gl)

		if (entalpha < 1)
		{
			gl.depthMask(true);
			gl.disable(gl.BLEND);
		}
	}


	GL.unbindProgram()
}

/*
================
R_DrawTextureChains_LitWater -- r_litwater

Turb surfaces with real lightmap samples (isLitWaterFlags) draw through the
Brush pipeline instead of Turbulent, so they pick up lightstyles/dlights/fog
like solid brushes. The diffuse UV is warped in-shader (uWarp) with the same
formula as fshTurbulent; lightmap UVs are sampled unwarped. Per-surface alpha
and blend state mirror drawTextureChains_water exactly.
================
*/
const drawTextureChains_litwater = (gl: WebGL2RenderingContext, model: Model, ent: Entity, chain: TexChain) => {
	if (!cvr.litwater.value || !cl.clState.worldmodel.haslitwater)
		return

	// No water chained for this model? Skip before touching the program, same
	// as drawTextureChains_water — the per-face isLitWaterFlags check below
	// decides which of those water surfaces actually belong to this pass.
	var anyWater = false
	var usedTex = model.usedTextures
	for (var ui = 0; ui < usedTex.length; ui++) {
		var t = model.textures[usedTex[ui]];
		if (t && t.texturechains && t.texturechains[chain]
			&& (t.texturechains[chain].flags & def.SURF.drawtub)) {
			anyWater = true
			break
		}
	}
	if (!anyWater)
		return

	var isWorld = model === cl.clState.worldmodel && chain === TexChain.world
	var visibleStamp = model.surfVisibleFrame, stampFrame = state.frustumFrame

	const brushProgram = GL.useProgram('Brush')
	const fogColor = fog.getColor()
	const fogDensity = fog.getDensity()

	// Bind the buffers
	gl.bindBuffer(gl.ARRAY_BUFFER, state.model_vbo);
	gl.bindBuffer(gl.ELEMENT_ARRAY_BUFFER, null) // indices come from client memory!

	gl.vertexAttribPointer(brushProgram.attributeMap.Vert.location, 3, gl.FLOAT, false, def.VERTEXSIZE * 4, 0);
	gl.vertexAttribPointer(brushProgram.attributeMap.TexCoords.location, 2, gl.FLOAT, false, def.VERTEXSIZE * 4, 4 * 3);
	gl.vertexAttribPointer(brushProgram.attributeMap.LMCoords.location, 2, gl.FLOAT, false, def.VERTEXSIZE * 4, 4 * 5);
	gl.vertexAttribPointer(brushProgram.attributeMap.LMStyles.location, 4, gl.FLOAT, false, def.VERTEXSIZE * 4, 4 * 7);

	// Same dirty-flag guard as drawTextureChains — cheap no-op if the solid
	// pass already uploaded this frame's lightstyle weights.
	if (lm.state.lightstyle_uniform_dirty) {
		const stylesUniform = lm.state.lightstyle_uniform
		for (var j = 0; j < lm.MAX_LIGHTSTYLES; j++)
			stylesUniform[j] = lm.state.lightstylevalue[j] / 128.0
		stylesUniform[64] = 0.0 // unused slot weight is always zero
		gl.uniform1fv(brushProgram.uniforms['uLightStyles[0]'], stylesUniform)
		lm.state.lightstyle_uniform_dirty = false
	}

	// set uniforms
	gl.uniform1i(brushProgram.uniforms.uUseFullbrightTex, 0);
	gl.uniform1i(brushProgram.uniforms.uUseOverbright, cvr.overbright.value);
	gl.uniform1i(brushProgram.uniforms.uUseAlphaTest, 0);
	gl.uniform1i(brushProgram.uniforms.uWarp, 1);
	gl.uniform1f(brushProgram.uniforms.uTime, host.state.realtime % (Math.PI * 2.0));
	gl.uniform1f(brushProgram.uniforms.uFogDensity, fogDensity / 64)
	gl.uniform4f(brushProgram.uniforms.uFogColor, fogColor[0], fogColor[1], fogColor[2], fogColor[3])
	tx.bind(2, tx.state.null_texture) // FullbrightTex unused on water (uUseFullbrightTex = 0)

	// GPU dlights: same once-per-frame guard as drawTextureChains.
	if (state.dlightUniformFrame !== state.framecount) {
		state.dlightUniformFrame = state.framecount;
		gl.uniform1i(brushProgram.uniforms.uNumDlights, state.numShaderDlights);
		gl.uniform4fv(brushProgram.uniforms['uDlightPosRadius[0]'], state.dlightPosRadius);
		gl.uniform4fv(brushProgram.uniforms['uDlightColor[0]'], state.dlightColor);
	}

	if (ent !== null) {
		var viewMatrix = GL.rotationMatrix(ent.angles[0], ent.angles[1], ent.angles[2]);

		gl.uniform3fv(brushProgram.uniforms.uOrigin, ent.origin);
		gl.uniformMatrix3fv(brushProgram.uniforms.uAngles, false, viewMatrix);

	} else {
		gl.uniform3f(brushProgram.uniforms.uOrigin, 0.0, 0.0, 0.0);
		gl.uniformMatrix3fv(brushProgram.uniforms.uAngles, false, GL.identity);
	}

	// World scans every texture slot (worldChainOfs/Count are indexed by
	// absolute texture index); entities scan only their own usedTextures.
	var texCount = isWorld ? model.textures.length : usedTex.length
	for (var ti = 0; ti < texCount; ti++) {
		var i = isWorld ? ti : usedTex[ti]
		var t = model.textures[i];
		if (!t || !t.texturechains || !t.texturechains[chain] || !(t.texturechains[chain].flags & def.SURF.drawtub))
			continue;

		var animatedTexture = textureAnimation(model, t, ent != null ? ent.frame : 0)
		batchRender.clearBatch();
		var bound = false;
		var lastlightmap = -1;
		var entalpha = 0

		if (isWorld) {
			var ofs = model.worldChainOfs[i], count = model.worldChainCount[i]
			var chainFaces = model.worldChainFaces, flagsArr = model.surfFlags, lmPage = model.surfLightmapPage
			for (var ci = 0; ci < count; ci++) {
				var f = chainFaces[ofs + ci]
				if (visibleStamp[f] !== stampFrame)
					continue
				if (!isLitWaterFlags(flagsArr[f]))
					continue
				if (!bound) {
					tx.bind(0, animatedTexture.texturenum);
					bound = true;
					lastlightmap = lmPage[f];
					bindLightmapPageTextures(gl, lastlightmap);
				}
				if (lmPage[f] !== lastlightmap) {
					batchRender.flushBatch(gl);
					bindLightmapPageTextures(gl, lmPage[f]);
					lastlightmap = lmPage[f];
				}
				var newalpha = waterAlphaForFlags(flagsArr[f]);
				if (newalpha !== entalpha)
					applyWaterAlpha(gl, brushProgram, newalpha);
				entalpha = newalpha
				batchRender.batchSurfaceRange(gl, model, f);
			}
		} else {
			for (var s = t.texturechains[chain]; s; s = s.texturechain) {
				if (!isLitWaterFlags(s.flags))
					continue
				if (!bound) {
					tx.bind(0, animatedTexture.texturenum);
					bound = true;
					lastlightmap = s.lightmaptexturenum;
					bindLightmapPageTextures(gl, lastlightmap);
				}
				if (s.lightmaptexturenum !== lastlightmap) {
					batchRender.flushBatch(gl);
					bindLightmapPageTextures(gl, s.lightmaptexturenum);
					lastlightmap = s.lightmaptexturenum;
				}
				var newalpha = waterAlphaForEntitySurface(ent, s);
				if (newalpha !== entalpha)
					applyWaterAlpha(gl, brushProgram, newalpha);
				entalpha = newalpha
				batchRender.batchSurface(gl, model, s);
			}
		}

		batchRender.flushBatch(gl)

		if (entalpha < 1)
		{
			gl.depthMask(true);
			gl.disable(gl.BLEND);
		}
	}

	GL.unbindProgram()
}

const drawTextureChains = (gl: WebGL2RenderingContext, model: Model, ent: Entity, chain: TexChain) => {
	var entalpha = ent ? pr.decodeAlpha(ent.alpha) : 1

	// See drawTextureChains_water for why only the world chain needs the
	// per-surface visibility check.
	var isWorld = model === cl.clState.worldmodel && chain === TexChain.world
	var visibleStamp = model.surfVisibleFrame, stampFrame = state.frustumFrame

	// Nothing lightmapped/solid chained for this model? Skip entirely — avoids
	// a Brush program bind per brush entity that has nothing for this pass.
	var anySolid = false
	var usedTex = model.usedTextures
	for (var ui = 0; ui < usedTex.length; ui++) {
		var t = model.textures[usedTex[ui]];
		if (t && t.texturechains && t.texturechains[chain]
			&& !(t.texturechains[chain].flags & (def.SURF.drawtiled | def.SURF.notexture | def.SURF.drawtub))) {
			anySolid = true
			break
		}
	}
	if (!anySolid)
		return

	// R_BeginTransparentDrawing (entalpha);

	// TODO: Missing texture support.
	// R_DrawTextureChains_NoTexture (model, chain);


	// R_EndTransparentDrawing (entalpha);

	// enable blending / disable depth writes
	if (entalpha < 1) {
		gl.depthMask(false);
		gl.enable(gl.BLEND);
	}

	const brushProgram = GL.useProgram('Brush')
	const fogColor = fog.getColor()
	const fogDensity = fog.getDensity()

	// Bind the buffers
	gl.bindBuffer(gl.ARRAY_BUFFER, state.model_vbo);
	gl.bindBuffer(gl.ELEMENT_ARRAY_BUFFER, null) // indices come from client memory!

	gl.vertexAttribPointer(brushProgram.attributeMap.Vert.location, 3, gl.FLOAT, false, def.VERTEXSIZE * 4, 0);
	gl.vertexAttribPointer(brushProgram.attributeMap.TexCoords.location, 2, gl.FLOAT, false, def.VERTEXSIZE * 4, 4 * 3);
	gl.vertexAttribPointer(brushProgram.attributeMap.LMCoords.location, 2, gl.FLOAT, false, def.VERTEXSIZE * 4, 4 * 5);
	gl.vertexAttribPointer(brushProgram.attributeMap.LMStyles.location, 4, gl.FLOAT, false, def.VERTEXSIZE * 4, 4 * 7);

	// Upload lightstyle weights only when a style value changed (10Hz ticks).
	// Uniform values persist on the program object across binds, so skipping
	// the upload on quiet frames is safe — and cheap: this used to run per
	// brush entity, dirtying the program's constant state on every rebind.
	if (lm.state.lightstyle_uniform_dirty) {
		const stylesUniform = lm.state.lightstyle_uniform
		for (var j = 0; j < lm.MAX_LIGHTSTYLES; j++)
			stylesUniform[j] = lm.state.lightstylevalue[j] / 128.0
		stylesUniform[64] = 0.0 // unused slot weight is always zero
		gl.uniform1fv(brushProgram.uniforms['uLightStyles[0]'], stylesUniform)
		lm.state.lightstyle_uniform_dirty = false
	}

	// set uniforms
	gl.uniform1i(brushProgram.uniforms.uUseFullbrightTex, 0);
	gl.uniform1i(brushProgram.uniforms.uUseOverbright, cvr.overbright.value);
	gl.uniform1i(brushProgram.uniforms.uUseAlphaTest, 0);
	// This pass never draws turb surfaces (excluded below), so warp stays off —
	// drawTextureChains_litwater is the only caller that turns it on.
	gl.uniform1i(brushProgram.uniforms.uWarp, 0);
	gl.uniform1f(brushProgram.uniforms.uAlpha, entalpha);
	gl.uniform1f(brushProgram.uniforms.uFogDensity, fogDensity / 64)
	gl.uniform4f(brushProgram.uniforms.uFogColor, fogColor[0], fogColor[1], fogColor[2], fogColor[3])

	// GPU dlights: packed once per frame in gatherDlights(); uniform values
	// persist on the program object across binds, so upload once per frame
	// even though drawTextureChains runs again for every brush entity.
	if (state.dlightUniformFrame !== state.framecount) {
		state.dlightUniformFrame = state.framecount;
		gl.uniform1i(brushProgram.uniforms.uNumDlights, state.numShaderDlights);
		gl.uniform4fv(brushProgram.uniforms['uDlightPosRadius[0]'], state.dlightPosRadius);
		gl.uniform4fv(brushProgram.uniforms['uDlightColor[0]'], state.dlightColor);
	}

	if (ent !== null) {
		var viewMatrix = GL.rotationMatrix(ent.angles[0], ent.angles[1], ent.angles[2]);

		gl.uniform3fv(brushProgram.uniforms.uOrigin, ent.origin);
		gl.uniformMatrix3fv(brushProgram.uniforms.uAngles, false, viewMatrix);

	} else {
		gl.uniform3f(brushProgram.uniforms.uOrigin, 0.0, 0.0, 0.0);
		gl.uniformMatrix3fv(brushProgram.uniforms.uAngles, false, GL.identity);
	}

	// World scans every texture slot (worldChainOfs/Count are indexed by
	// absolute texture index); entities scan only their own usedTextures.
	var texCount = isWorld ? model.textures.length : usedTex.length
	for (var ti = 0; ti < texCount; ti++) {
		var i = isWorld ? ti : usedTex[ti]
		var t = model.textures[i];

		if (!t || !t.texturechains || !t.texturechains[chain] || t.texturechains[chain].flags & (def.SURF.drawtiled | def.SURF.notexture | def.SURF.drawtub))
			continue;

		var animatedTexture = textureAnimation(model, t, ent != null ? ent.frame : 0)

		batchRender.clearBatch();

		var bound = false;
		var lastlightmap = -1;

		if (isWorld) {
			var ofs = model.worldChainOfs[i], count = model.worldChainCount[i]
			var chainFaces = model.worldChainFaces, lmPage = model.surfLightmapPage
			for (var ci = 0; ci < count; ci++) {
				var f = chainFaces[ofs + ci]
				if (visibleStamp[f] !== stampFrame)
					continue
				if (!bound) //only bind once we are sure we need this texture
				{
					tx.bind(0, animatedTexture.texturenum);
					bindFullbrightTexture(gl, brushProgram, animatedTexture);

					if (t.texturechains[chain].flags & def.SURF.drawfence)
						gl.uniform1i(brushProgram.uniforms.uUseAlphaTest, 1);

					bound = true;
					lastlightmap = lmPage[f];
					bindLightmapPageTextures(gl, lastlightmap);
				}

				if (lmPage[f] !== lastlightmap) {
					batchRender.flushBatch(gl);
					bindLightmapPageTextures(gl, lmPage[f]);
					lastlightmap = lmPage[f];
				}

				batchRender.batchSurfaceRange(gl, model, f);

				// rs_brushpasses++; // stats
			}
		} else {
			for (var s = t.texturechains[chain]; !!s; s = s.texturechain) {
				if (!bound) //only bind once we are sure we need this texture
				{
					tx.bind(0, animatedTexture.texturenum);
					bindFullbrightTexture(gl, brushProgram, animatedTexture);

					if (t.texturechains[chain].flags & def.SURF.drawfence)
						gl.uniform1i(brushProgram.uniforms.uUseAlphaTest, 1);

					bound = true;
					lastlightmap = s.lightmaptexturenum;
					bindLightmapPageTextures(gl, s.lightmaptexturenum);
				}

				if (s.lightmaptexturenum !== lastlightmap) {
					batchRender.flushBatch(gl);
					bindLightmapPageTextures(gl, s.lightmaptexturenum);
					lastlightmap = s.lightmaptexturenum;
				}

				batchRender.batchSurface(gl, model, s);

				// rs_brushpasses++; // stats
			}
		}

		batchRender.flushBatch(gl);

		if (bound && t.texturechains[chain].flags & def.SURF.drawfence)
			gl.uniform1i(brushProgram.uniforms.uUseAlphaTest, 0); // Flip alpha test back off
	}

	GL.unbindProgram()

	if (entalpha < 1) {
		gl.depthMask(true);
		gl.disable(gl.BLEND);
	}
}

// Marks the leaf, then walks nodeParent to the root, stopping at the first
// already-marked node.
const markAncestorsVisible = (model: Model, leafNum: number, visframecount: number) => {
	if (leafNum >= model.leafMarkvisframe.length)
		return;
	if (model.leafMarkvisframe[leafNum] === visframecount)
		return;
	model.leafMarkvisframe[leafNum] = visframecount;
	for (var n = model.leafParent[leafNum]; n >= 0; n = model.nodeParent[n]) {
		if (model.nodeMarkvisframe[n] === visframecount)
			break;
		model.nodeMarkvisframe[n] = visframecount;
	}
}

const noVisPVS = (model: Model) => {
	const pvsbytes = (model.numleafs + 7) >> 3;
	if (!state.mod_novis || pvsbytes > state.mod_novis_capacity) {
		state.mod_novis_capacity = pvsbytes;
		state.mod_novis = new Uint8Array(new ArrayBuffer(state.mod_novis_capacity))
		state.mod_novis.fill(0xFF)
	}
	return state.mod_novis;
}

const leafPVS = (leaf: Leaf, model: Model) => {
	if (leaf == model.leafs[0])
		return noVisPVS(model);
	// shared scratch: markSurfaces copies what it caches, so no caller may retain this
	const row = mod.visRowBytes(model)
	if (!state.leafpvs_scratch || state.leafpvs_scratch.length < row)
		state.leafpvs_scratch = new Uint8Array(row)
	return mod.decompressVis(leaf.visofs, model, state.leafpvs_scratch);
}

// The PVS must include a small area around the client to allow head bobbing
// or other small motion on the client side.  Otherwise, a bob might cause an
// entity that should be visible to not show up, especially when the bob
// crosses a waterline.

const addToFatPVS = (org: V3, node: NodeLeaf, worldmodel: Model) => { // johnfitz -- added worldmodel as a parameter

	while (1) {
		// if this is a leaf, accumulate the pvs bits
		if (node.contents < 0) {
			const leaf = node as Leaf;
			if (node.contents !== mod.CONTENTS.solid) {
				// decompress into the persistent scratch buffer — this runs every frame
				// while near water, so a fresh allocation per leaf adds up
				const pvs = mod.decompressVis(leaf.visofs, worldmodel, state.fatpvs_scratch);
				for (var i = 0; i < state.fatbytes; i++)
					state.fatpvs[i] |= pvs[i];
			}
			return;
		}

		const _node = node as Node;
		const plane = _node.plane;
		const d = vec.dotProductV3(org, plane.normal) - plane.dist;
		if (d > 8)
			node = _node.children[0];
		else if (d < -8)
			node = _node.children[1];
		else {	// go down both
			addToFatPVS(org, _node.children[0], worldmodel); //johnfitz -- worldmodel as a parameter
			node = _node.children[1];
		}
	}
}

// Calculates a PVS that is the inclusive or of all leafs within 8 pixels of the
// given point.
const fatPVS = (org: V3, worldmodel: Model) => //johnfitz -- added worldmodel as a parameter
{
	state.fatbytes = (worldmodel.numleafs + 7) >> 3; // ericw -- was +31, assumed to be a bug/typo
	if (!state.fatpvs || state.fatbytes > state.fatpvs_capacity) {
		state.fatpvs_capacity = state.fatbytes;
		state.fatpvs = new Uint8Array(new ArrayBuffer(state.fatpvs_capacity))
	}
	const scratchbytes = mod.visRowBytes(worldmodel)
	if (!state.fatpvs_scratch || state.fatpvs_scratch.length < scratchbytes)
		state.fatpvs_scratch = new Uint8Array(scratchbytes)
	state.fatpvs.fill(0, 0, state.fatbytes)
	addToFatPVS(org, worldmodel.nodes[0], worldmodel); //johnfitz -- worldmodel as a parameter
	return state.fatpvs;
}

const visEquals = (a: Uint8Array, b: Uint8Array, nbytes: number) => {
	if (a === b)
		return true
	if (a.length < nbytes || b.length < nbytes)
		return false
	for (var i = 0; i < nbytes; i++)
		if (a[i] !== b[i])
			return false
	return true
}

const chainSurface = (model: Model, surf: Face, chain: TexChain) => {
	const texture = model.textures[model.texinfo[surf.texinfo].texture]
	surf.texturechain = texture.texturechains[chain];
	texture.texturechains[chain] = surf;
}

const markSurfaces = () => {
	var vis: Uint8Array 
	const worldmodel = cl.clState.worldmodel
	// check this leaf for water portals
	// TODO: loop through all water surfs and use distance to leaf cullbox
	var nearwaterportal = false;
	for (var i = 0, mark = state.viewleaf.firstmarksurface; i < state.viewleaf.nummarksurfaces; i++ , mark++)
		if (worldmodel.faces[worldmodel.marksurfaces[mark]].flags & def.SURF.drawtub)
			nearwaterportal = true;

	// if surface chains don't need regenerating, just add static entities and return
	// (reuse the vis decompressed when the chains were last rebuilt — decompressing
	// the PVS every frame allocates and burns CPU on large maps)
	if (state.oldviewleaf == state.viewleaf && !state.vis_changed && state.cached_vis) {
		var reuse = !nearwaterportal
		if (!reuse && !cvr.novis.value && state.viewleaf.contents !== mod.CONTENTS.solid && state.viewleaf.contents !== mod.CONTENTS.sky) {
			// near water the PVS is position-dependent (fatPVS), so it must be recomputed
			// every frame — but while its bytes are unchanged the chains are still valid,
			// and this compare is far cheaper than the re-mark + chain rebuild below
			reuse = visEquals(fatPVS(state.refdef.vieworg, worldmodel), state.cached_vis, state.fatbytes)
		}
		if (reuse) {
			vis = state.cached_vis
			for (i = 0; i < worldmodel.numleafs; i++) {
				var leaf = worldmodel.leafs[i + 1];
				if (vis[i>>3] & (1<<(i&7)))
					if (leaf.efrags)
						storeEfrags (leaf.efrags);
			}
			return;
		}
	}

	// choose vis data
	if (cvr.novis.value || state.viewleaf.contents === mod.CONTENTS.solid || state.viewleaf.contents === mod.CONTENTS.sky)
		vis = noVisPVS(worldmodel);
	else if (nearwaterportal)
		vis = fatPVS(state.refdef.vieworg, worldmodel);
	else {
		vis = leafPVS(state.viewleaf, worldmodel);
		// The viewleaf changed but its decompressed PVS row is byte-identical to
		// the one the chains were built from (common for adjacent leafs on large
		// maps) — the chains are still valid, so skip the O(visible) re-mark and
		// rebuild. The compare is ~rowbytes; the rebuild is tens of ms on 1M+
		// face maps.
		if (!state.vis_changed && state.cached_vis && visEquals(vis, state.cached_vis, mod.visRowBytes(worldmodel))) {
			state.oldviewleaf = state.viewleaf
			for (i = 0; i < worldmodel.numleafs; i++) {
				var sleaf = worldmodel.leafs[i + 1];
				if ((vis[i >> 3] & (1 << (i & 7))) && sleaf.efrags)
					storeEfrags(sleaf.efrags);
			}
			return;
		}
	}
	// every vis source above lives in a persistent shared buffer that later rebuilds
	// overwrite -- cached_vis keeps its own persistent copy for the reuse compare
	var rowbytes = mod.visRowBytes(worldmodel);
	if (state.cached_vis == null || state.cached_vis.length < rowbytes)
		state.cached_vis = new Uint8Array(rowbytes);
	state.cached_vis.set(vis.length <= rowbytes ? vis : vis.subarray(0, rowbytes));

	state.vis_changed = false
	state.visframecount++;
	state.oldviewleaf = state.viewleaf
	state.rs_rebuilds++

	// set all chains to null
	for (i = 0; i < worldmodel.textures.length; i++)
		if (worldmodel.textures[i] && worldmodel.textures[i].texturechains)
			worldmodel.textures[i].texturechains[TexChain.world] = null;

	// Iterate through leaves, marking surfaces and chaining them as they're
	// first seen (QSS-M r_world.c R_MarkSurfaces order) — the visframe stamp
	// doubles as the dedup guard for faces shared between leafs. This replaces
	// the old FitzQuake full node/face sweep, which cost O(all faces) per
	// rebuild regardless of how few were visible.
	for (i = 0; i < worldmodel.numleafs; i++) {
		var leaf = worldmodel.leafs[i + 1];
		if (vis[i >> 3] & (1 << (i & 7))) {
			// stamps leafMarkvisframe/nodeMarkvisframe up to the root — the
			// hierarchical frustum walk (markWorldFrustum) relies on these to
			// know which subtrees are in the current PVS
			markAncestorsVisible(worldmodel, i + 1, state.visframecount);

			if (cvr.oldskyleaf.value || leaf.contents != mod.CONTENTS.sky)
				for (var j = 0; j < leaf.nummarksurfaces; j++) {
					const snum = worldmodel.marksurfaces[leaf.firstmarksurface + j]
					if (worldmodel.surfVisframe[snum] !== state.visframecount) {
						worldmodel.surfVisframe[snum] = state.visframecount;
						chainSurface(worldmodel, worldmodel.faces[snum], TexChain.world);
					}
				}

			// add static models
			if (leaf.efrags)
				storeEfrags (leaf.efrags);
		}
	}

	flattenWorldChains(worldmodel)

	state.drawsky = true
}

// Mirrors the just-rebuilt TexChain.world linked lists into worldChainFaces
// (grouped by texture, worldChainOfs/Count give each texture's range) so the
// per-frame draw loops can stream a typed array instead of chasing pointers.
// Only runs when markSurfaces rebuilds the chains (PVS change) — the linked
// lists remain the source of truth.
const flattenWorldChains = (model: Model) => {
	var cursor = 0
	for (var i = 0; i < model.textures.length; i++) {
		var t = model.textures[i]
		model.worldChainOfs[i] = cursor
		if (t && t.texturechains)
			for (var s = t.texturechains[TexChain.world]; s; s = s.texturechain)
				model.worldChainFaces[cursor++] = s.num
		model.worldChainCount[i] = cursor - model.worldChainOfs[i]
	}
}

// reused per-vertex scratch for buildSurfaceDisplayLists (cold: runs at newMap)
const dispVertScratch: V3 = [0, 0, 0]
const buildSurfaceDisplayLists = (model: Model) => {
	for (var i = 0; i < model.numfaces; i++) {

		if ((model.faces[i].flags & def.SURF.drawtiled) && !(model.faces[i].flags & def.SURF.drawtub))
			continue;

		var fa = model.faces[i]
		const S = def.POLY_VERT_STRIDE
		const pverts = model.polyVertData
		const pbase = model.surfVertOfs[i] * S

		const texInfo = model.texinfo[fa.texinfo]
		const texture = model.textures[texInfo.texture]
		const lmscale = 1 << fa.lmshift // texture units per luxel (BSPX LMSHIFT; 16 = vanilla)

		for (var j = 0; j < fa.numedges; j++) {
			var s, t
			const _vec = dispVertScratch
			mod.surfedgeVertexInto(model, model.surfedges[fa.firstedge + j], _vec);
			//@ts-ignore
			s = vec.dotProductV3(_vec, texInfo.vecs[0]) + texInfo.vecs[0][3];
			s /= texture.width;
			//@ts-ignore
			t = vec.dotProductV3(_vec, texInfo.vecs[1]) + texInfo.vecs[1][3];
			t /= texture.height;

			var vb = pbase + j * S
			pverts[vb] = _vec[0]; pverts[vb + 1] = _vec[1]; pverts[vb + 2] = _vec[2];
			pverts[vb + 3] = s;
			pverts[vb + 4] = t;

			//
			// lightmap texture coordinates
			//
			if (fa.decoupled) {
				// lmvecs give luxel coords directly (texturemins folded into .w);
				// the classic path below reduces to this once divided by lmscale.
				const lv = fa.lmvecs as Float32Array
				//@ts-ignore
				s = _vec[0] * lv[0] + _vec[1] * lv[1] + _vec[2] * lv[2] + lv[3]
				s += fa.light_s
				s += 0.5
				s /= lm.LM_BLOCK_WIDTH
				//@ts-ignore
				t = _vec[0] * lv[4] + _vec[1] * lv[5] + _vec[2] * lv[6] + lv[7]
				t += fa.light_t
				t += 0.5
				t /= lm.LM_BLOCK_HEIGHT
			} else {
				//@ts-ignore
				s = vec.dotProductV3(_vec, texInfo.vecs[0]) + texInfo.vecs[0][3];
				s -= fa.texturemins[0];
				s += fa.light_s * lmscale;
				s += lmscale / 2;
				s /= lm.LM_BLOCK_WIDTH * lmscale; //fa->texinfo->texture->width;

				//@ts-ignore
				t = vec.dotProductV3(_vec, texInfo.vecs[1]) + texInfo.vecs[1][3];
				t -= fa.texturemins[1];
				t += fa.light_t * lmscale;
				t += lmscale / 2;
				t /= lm.LM_BLOCK_HEIGHT * lmscale; //fa->texinfo->texture->height;
			}

			pverts[vb + 5] = s;
			pverts[vb + 6] = t;
		}

		//johnfitz -- removed gl_keeptjunctions code
	}
}

const buildModelVertexBuffer = (gl: WebGLRenderingContext) => {
	// Pass 1: exact vertex count. Pre-sizing the Float32Array avoids staging the
	// whole VBO in a growing JS number[] first — on a 1.7M-face BSP2 that
	// intermediate is a transient GB-scale allocation.
	var total = 0
	for (var midx = 1; midx < cl.clState.model_precache.length; ++midx) {
		var model = cl.clState.model_precache[midx];
		if (!model || model.name[0] == '*' || model.type != mod.TYPE.brush)
			continue;
		total += model.polyVertData.length / def.POLY_VERT_STRIDE
	}

	var v_buffer = new Float32Array(total * def.VERTEXSIZE)
	var o = 0
	var v_index = 0

	for (var midx = 1; midx < cl.clState.model_precache.length; ++midx) {
		var model = cl.clState.model_precache[midx];
		if (!model || model.name[0] == '*' || model.type != mod.TYPE.brush)
			continue;

		for (var i = 0; i < model.faces.length; i++) {
			const surf = model.faces[i]
			surf.vbo_firstvert = v_index
			const pv = model.polyVertData, nv = surf.numedges, S = def.POLY_VERT_STRIDE
			var b = model.surfVertOfs[i] * S
			const s0 = surf.styles[0] != null ? surf.styles[0] : 64
			const s1 = surf.styles[1] != null ? surf.styles[1] : 64
			const s2 = surf.styles[2] != null ? surf.styles[2] : 64
			const s3 = surf.styles[3] != null ? surf.styles[3] : 64
			for (var j = 0; j < nv; j++) {
				for (var k = 0; k < 7; k++)
					v_buffer[o++] = pv[b++]
				// 4 lightstyle indices (slots 0-3); 64 = unused slot (GPU weight = 0)
				v_buffer[o++] = s0
				v_buffer[o++] = s1
				v_buffer[o++] = s2
				v_buffer[o++] = s3
			}

			v_index += nv

			// Fill the prebuilt fan indices (ofs/count already computed at load
			// time in mod.loadBrushModel) now that vbo_firstvert is final.
			var idx = model.surfIndexOfs[i]
			for (var e = 2; e < surf.numedges; e++) {
				model.surfIndexData[idx++] = surf.vbo_firstvert
				model.surfIndexData[idx++] = surf.vbo_firstvert + e - 1
				model.surfIndexData[idx++] = surf.vbo_firstvert + e
			}
		}
	}

	state.model_vbo = gl.createBuffer();
	gl.bindBuffer(gl.ARRAY_BUFFER, state.model_vbo);
	gl.bufferData(gl.ARRAY_BUFFER, v_buffer, gl.STATIC_DRAW);
}

export const freeResources = () => {
	const gl = GL.getContext()
	gl.deleteFramebuffer(state.warpbuffer)
	gl.deleteBuffer(state.model_vbo)
	gl.deleteBuffer(state.skyvecs)
	gl.deleteBuffer(state.dlightvecs)
	gl.deleteRenderbuffer(state.warprenderbuffer)

	gl.deleteTexture(state.notexture_mip && state.notexture_mip.texturenum)
	gl.deleteTexture(state.warptexture)
	gl.deleteTexture(state.solidskytexture)
	gl.deleteTexture(state.alphaskytexture)
	gl.deleteTexture(state.null_texture)

	if (sky.state.texture) {
		gl.deleteTexture(sky.state.texture)
		sky.state.texture = null
	}
	sky.state.name = ''
	sky.state.generation++ // cancel any load still in flight
}

// scan

export const warpScreen = function () {
	const gl = GL.getContext()
	GL.streamFlush();
	gl.flush(); // ensure all FBO commands reach the GPU before switching framebuffers
	gl.bindFramebuffer(gl.FRAMEBUFFER, null);
	gl.bindRenderbuffer(gl.RENDERBUFFER, null);
	// The warp quad is a fullscreen 2D blit — depth testing is irrelevant and can
	// silently discard the quad on some Android drivers if the depth buffer holds
	// stale values from a previous frame.
	gl.disable(gl.DEPTH_TEST);
	var program = GL.useProgram('Warp');
	tx.bind(program.textures.tTexture, state.warptexture);
	gl.uniform1f(program.uniforms.uTime, host.state.realtime % (Math.PI * 2.0));
	var vrect = state.refdef.vrect;
	GL.streamDrawTexturedQuad(vrect.x, vrect.y, vrect.width, vrect.height, 0.0, 1.0, 1.0, 0.0);
	GL.streamFlush();
};

// warp

export const makeSky = function () {
	const gl = GL.getContext()
	var sin = [0.0, 0.19509, 0.382683, 0.55557, 0.707107, 0.831470, 0.92388, 0.980785, 1.0];
	var vecs: number[] = [], i, j;

	for (i = 0; i < 7; i += 2) {
		vecs = vecs.concat(
			[
				0.0, 0.0, 1.0,
				sin[i + 2] * 0.19509, sin[6 - i] * 0.19509, 0.980785,
				sin[i] * 0.19509, sin[8 - i] * 0.19509, 0.980785
			]);
		for (j = 0; j < 7; ++j) {
			vecs = vecs.concat(
				[
					sin[i] * sin[8 - j], sin[8 - i] * sin[8 - j], sin[j],
					sin[i] * sin[7 - j], sin[8 - i] * sin[7 - j], sin[j + 1],
					sin[i + 2] * sin[7 - j], sin[6 - i] * sin[7 - j], sin[j + 1],

					sin[i] * sin[8 - j], sin[8 - i] * sin[8 - j], sin[j],
					sin[i + 2] * sin[7 - j], sin[6 - i] * sin[7 - j], sin[j + 1],
					sin[i + 2] * sin[8 - j], sin[6 - i] * sin[8 - j], sin[j]
				]);
		}
	}

	GL.createProgram('Sky',
		['uViewAngles', 'uPerspective', 'uScale', 'uGamma', 'uTime'],
		[
			createAttribParam('aPosition', gl.FLOAT, 3)
		],
		['tSolid', 'tAlpha']);
	GL.createProgram('SkyChain',
		['uViewOrigin', 'uViewAngles', 'uPerspective'],
		[
			createAttribParam('aPosition', gl.FLOAT, 3)
		],
		[]);
	GL.createProgram('SkyCube',
		['uViewOrigin', 'uViewAngles', 'uPerspective', 'uGamma', 'uSkyFog', 'uFogColor'],
		[
			createAttribParam('aPosition', gl.FLOAT, 3)
		],
		['tSky']);

	state.skyvecs = gl.createBuffer();
	gl.bindBuffer(gl.ARRAY_BUFFER, state.skyvecs);
	gl.bufferData(gl.ARRAY_BUFFER, new Float32Array(vecs), gl.STATIC_DRAW);
};

export const drawSkyBox = function () {
	const gl = GL.getContext()
	if (state.drawsky !== true)
		return;

	// A skyroom was drawn underneath this frame: write sky-surface DEPTH only (no color)
	// so world geometry behind the windows can't overdraw the skyroom, while the windows
	// keep the skyroom color composited earlier. Replaces the cubemap/classic sky here
	// (QSS gl_sky.c:1179 Sky_DrawSky skyroom_drawn branch).
	if (sky.state.skyroom_drawn) {
		var clmodel = cl.clState.worldmodel;
		var visibleStamp = clmodel.surfVisibleFrame, stampFrame = state.frustumFrame;
		var chainFaces = clmodel.worldChainFaces;
		gl.colorMask(false, false, false, false);
		var depthProgram = GL.useProgram('SkyChain', false);
		gl.bindBuffer(gl.ARRAY_BUFFER, state.model_vbo);
		gl.bindBuffer(gl.ELEMENT_ARRAY_BUFFER, null)
		gl.vertexAttribPointer(depthProgram.attributeMap.aPosition.location, 3, gl.FLOAT, false, def.VERTEXSIZE * 4, 0);
		for (var i = 0; i < clmodel.textures.length; i++) {
			var t = clmodel.textures[i];
			if (!t || !t.texturechains || !t.texturechains[TexChain.world] || !(t.texturechains[TexChain.world].flags & def.SURF.drawsky))
				continue;
			var ofs = clmodel.worldChainOfs[i], count = clmodel.worldChainCount[i]
			for (var ci = 0; ci < count; ci++) {
				var f = chainFaces[ofs + ci]
				if (visibleStamp[f] === stampFrame) {
					sky.state.skyVisibleThisFrame = true; // keep the skyroom alive next frame
					batchRender.batchSurfaceRange(gl, clmodel, f);
				}
			}
		}
		batchRender.flushBatch(gl);
		gl.colorMask(true, true, true, true);
		return;
	}

	if (sky.state.texture !== null) {
		var clmodel = cl.clState.worldmodel;
		var cubeProgram = GL.useProgram('SkyCube', false);
		gl.bindBuffer(gl.ARRAY_BUFFER, state.model_vbo);
		gl.bindBuffer(gl.ELEMENT_ARRAY_BUFFER, null)
		gl.vertexAttribPointer(cubeProgram.attributeMap.aPosition.location, 3, gl.FLOAT, false, def.VERTEXSIZE * 4, 0);

		// tx.bind only handles TEXTURE_2D; bind the cubemap manually and mirror
		// the state into tx's per-unit cache so a later tx.bind on this unit
		// doesn't wrongly skip its own gl.activeTexture/gl.bindTexture calls.
		var skyUnit = cubeProgram.textures.tSky;
		gl.activeTexture(gl.TEXTURE0 + skyUnit);
		gl.bindTexture(gl.TEXTURE_CUBE_MAP, sky.state.texture);
		tx.state.activetexture = skyUnit;
		tx.state.currenttextures[skyUnit] = sky.state.texture;

		var fogDensity = fog.getDensity();
		var fogColor = fog.getColor();
		var skyFog = fogDensity <= 0 ? 0 : clamp(0, sky.state.skyfog, 1);
		gl.uniform1f(cubeProgram.uniforms.uSkyFog, skyFog);
		gl.uniform3f(cubeProgram.uniforms.uFogColor, fogColor[0], fogColor[1], fogColor[2]);

		var visibleStamp = clmodel.surfVisibleFrame, stampFrame = state.frustumFrame;
		var chainFaces = clmodel.worldChainFaces;
		for (var i = 0; i < clmodel.textures.length; i++) {
			var t = clmodel.textures[i];
			if (!t || !t.texturechains || !t.texturechains[TexChain.world] || !(t.texturechains[TexChain.world].flags & def.SURF.drawsky))
				continue;
			var ofs = clmodel.worldChainOfs[i], count = clmodel.worldChainCount[i]
			for (var ci = 0; ci < count; ci++) {
				var f = chainFaces[ofs + ci]
				if (visibleStamp[f] === stampFrame) {
					sky.state.skyVisibleThisFrame = true;
					batchRender.batchSurfaceRange(gl, clmodel, f);
				}
			}
		}
		batchRender.flushBatch(gl);
		return;
	}

	gl.colorMask(false, false, false, false);
	var clmodel = cl.clState.worldmodel;
	var program = GL.useProgram('SkyChain', false);
	gl.bindBuffer(gl.ARRAY_BUFFER,  state.model_vbo);
	gl.bindBuffer(gl.ELEMENT_ARRAY_BUFFER, null)
	gl.vertexAttribPointer(program.attributeMap.aPosition.location, 3, gl.FLOAT, false, def.VERTEXSIZE * 4, 0);
	var visibleStamp = clmodel.surfVisibleFrame, stampFrame = state.frustumFrame;
	var chainFaces = clmodel.worldChainFaces;
	for (var i = 0; i < clmodel.textures.length; i++) {
		var t = clmodel.textures[i];
		if (!t || !t.texturechains || !t.texturechains[TexChain.world] || !(t.texturechains[TexChain.world].flags & def.SURF.drawsky))
			continue;
		var ofs = clmodel.worldChainOfs[i], count = clmodel.worldChainCount[i]
		for (var ci = 0; ci < count; ci++) {
			var f = chainFaces[ofs + ci]
			if (visibleStamp[f] === stampFrame) {
				sky.state.skyVisibleThisFrame = true;
				batchRender.batchSurfaceRange(gl, clmodel, f);
			}
		}
	}
	batchRender.flushBatch(gl);

	gl.colorMask(true, true, true, true);

	gl.depthFunc(gl.GREATER);
	gl.depthMask(false);
	gl.disable(gl.CULL_FACE);

	program = GL.useProgram('Sky', false);
	gl.uniform2f(program.uniforms.uTime, (host.state.realtime * 0.125) % 1.0, (host.state.realtime * 0.03125) % 1.0);
	tx.bind(program.textures.tSolid, state.solidskytexture, false);
	tx.bind(program.textures.tAlpha, state.alphaskytexture, false);
	gl.bindBuffer(gl.ARRAY_BUFFER, state.skyvecs);
	gl.vertexAttribPointer(program.attributeMap.aPosition.location, 3, gl.FLOAT, false, 12, 0);

	gl.uniform3f(program.uniforms.uScale, 2.0, -2.0, 1.0);
	gl.drawArrays(gl.TRIANGLES, 0, 180);
	gl.uniform3f(program.uniforms.uScale, 2.0, -2.0, -1.0);
	gl.drawArrays(gl.TRIANGLES, 0, 180);

	gl.uniform3f(program.uniforms.uScale, 2.0, 2.0, 1.0);
	gl.drawArrays(gl.TRIANGLES, 0, 180);
	gl.uniform3f(program.uniforms.uScale, 2.0, 2.0, -1.0);
	gl.drawArrays(gl.TRIANGLES, 0, 180);

	gl.uniform3f(program.uniforms.uScale, -2.0, -2.0, 1.0);
	gl.drawArrays(gl.TRIANGLES, 0, 180);
	gl.uniform3f(program.uniforms.uScale, -2.0, -2.0, -1.0);
	gl.drawArrays(gl.TRIANGLES, 0, 180);

	gl.uniform3f(program.uniforms.uScale, -2.0, 2.0, 1.0);
	gl.drawArrays(gl.TRIANGLES, 0, 180);
	gl.uniform3f(program.uniforms.uScale, -2.0, 2.0, -1.0);
	gl.drawArrays(gl.TRIANGLES, 0, 180);

	gl.enable(gl.CULL_FACE);
	gl.depthMask(true);
	gl.depthFunc(gl.LESS);
};

export const initSky = function (src: Uint8Array) {
	const gl = GL.getContext()
	var i, j, p;
	var trans = new ArrayBuffer(65536);
	var trans32 = new Uint32Array(trans);

	for (i = 0; i < 128; ++i) {
		for (j = 0; j < 128; ++j)
			trans32[(i << 7) + j] = com.state.littleLong(vid.d_8to24table[src[(i << 8) + j + 128]] + 0xff000000);
	}
	if (gl) {
		tx.bind(0, state.solidskytexture, false);
		gl.texImage2D(gl.TEXTURE_2D, 0, gl.RGBA, 128, 128, 0, gl.RGBA, gl.UNSIGNED_BYTE, new Uint8Array(trans));
		gl.generateMipmap(gl.TEXTURE_2D);

		for (i = 0; i < 128; ++i) {
			for (j = 0; j < 128; ++j) {
				p = (i << 8) + j;
				if (src[p] !== 0)
					trans32[(i << 7) + j] = com.state.littleLong(vid.d_8to24table[src[p]] + 0xff000000);
				else
					trans32[(i << 7) + j] = 0;
			}
		}
		tx.bind(0, state.alphaskytexture, false);
		gl.texImage2D(gl.TEXTURE_2D, 0, gl.RGBA, 128, 128, 0, gl.RGBA, gl.UNSIGNED_BYTE, new Uint8Array(trans));
		gl.generateMipmap(gl.TEXTURE_2D);
	}
}
